import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppContext, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

// Clipboard utility - works in all browsers including iframes
const copyToClipboard = async (text, successMessage = 'Copied!') => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    } catch (err) {
      console.log('Clipboard API failed, using fallback');
    }
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (successful) {
      toast.success(successMessage);
      return true;
    }
  } catch (err) {}
  toast.error('Copy failed. Please select and copy manually.');
  return false;
};

import { 
  Activity, 
  Bell, 
  Search, 
  TrendingUp, 
  TrendingDown,
  Volume2,
  Plus,
  X,
  RefreshCw,
  Trash2,
  BarChart3,
  LogOut,
  Filter,
  ArrowUpDown,
  Settings2,
  History,
  CalendarDays,
  Scan,
  ChevronDown,
  ZoomIn,
  ZoomOut,
  Pause,
  Play,
  Clock,
  Server,
  Wifi,
  WifiOff,
  Gift,
  Copy,
  Users,
  Share2,
  Layers,
  Brain,
  AlertCircle,
  Zap,
  Key,
  Table2,
  Youtube,
  ExternalLink,
  Link2,
  Check,
  Crown,
  Edit3,
  Sparkles,
  BellRing
} from "lucide-react";
import TickHistory from "../components/TickHistory";
import HistoryTab from "../components/HistoryTab";
import ScannerPanel from "../components/ScannerPanel";
import SubscriptionModal from "../components/SubscriptionModal";
import GlobalSearch from "../components/GlobalSearch";
import ReferralDashboard from "../components/ReferralDashboard";
import AlgoSetupTab from "../components/AlgoSetupTab";
import AlgoMonitor from "../components/AlgoMonitor";
import AdminApprovalModal from "../components/AdminApprovalModal";
import ServerAlertManager from "../components/ServerAlertManager";

// Referral Widget Component
const ReferralWidget = ({ userId, user }) => {
  const [referralData, setReferralData] = useState(null);
  const [showReferralPanel, setShowReferralPanel] = useState(false);
  const [showFullDashboard, setShowFullDashboard] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadReferralData = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        // Get user's referral code
        const codeResponse = await apiClient.get(`/referral/user-code/${userId}`);
        const statsResponse = await apiClient.get(`/referral/user-stats/${userId}`);
        setReferralData({
          code: codeResponse.data.referral_code,
          ...statsResponse.data
        });
      } catch (err) {
        console.error('Failed to load referral data:', err);
        // Don't hide the widget on error - show a loading/retry state instead
      } finally {
        setIsLoading(false);
      }
    };
    loadReferralData();
  }, [userId]);
  
  const copyReferralCode = () => {
    if (referralData?.code) {
      copyToClipboard(referralData.code, 'Referral code copied!');
    }
  };
  
  const copyReferralLink = () => {
    if (referralData?.code) {
      const link = `${window.location.origin}?ref=${referralData.code}`;
      copyToClipboard(link, 'Referral link copied! Share it with friends.');
    }
  };
  
  const shareViaWhatsApp = () => {
    if (referralData?.code) {
      const link = `${window.location.origin}?ref=${referralData.code}`;
      const message = encodeURIComponent(`Join AlphaTradeVission Ticks PRO! Use my referral code: ${referralData.code}\n\n${link}`);
      window.open(`https://wa.me/?text=${message}`, '_blank');
    }
  };
  
  // Always show the button, but show loading state while fetching
  // Only hide if userId is not provided
  if (!userId) return null;
  
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowReferralPanel(!showReferralPanel)}
        className="h-9 px-3 text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
        data-testid="referral-btn"
      >
        <Gift className="w-4 h-4 mr-2" />
        {isLoading ? 'Loading...' : 'Refer & Earn'}
      </Button>
      
      {showReferralPanel && (
        <div className="fixed right-4 top-28 w-80 bg-[#111827] border border-[#1a2540] rounded-xl shadow-2xl z-[100] p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Gift className="w-4 h-4 text-amber-400" />
              Your Referral Program
            </h3>
            <button onClick={() => setShowReferralPanel(false)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {isLoading ? (
            <div className="text-center py-4">
              <RefreshCw className="w-6 h-6 text-cyan-400 animate-spin mx-auto mb-2" />
              <p className="text-sm text-[#6b7a99]">Loading referral data...</p>
            </div>
          ) : referralData ? (
            <>
              {/* Referral Code */}
              <div className="bg-[#0a0e17] rounded-lg p-3 mb-3">
                <p className="text-[10px] text-[#6b7a99] mb-1">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="text-lg font-mono font-bold text-cyan-400 flex-1">{referralData.code}</code>
                  <button 
                    onClick={copyReferralCode}
                    className="p-1.5 hover:bg-[#1a2540] rounded transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
                  </button>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-green-400">{referralData.successful_referrals || 0}</p>
                  <p className="text-[9px] text-[#6b7a99]">Successful</p>
                </div>
                <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-yellow-400">{referralData.pending_referrals || 0}</p>
                  <p className="text-[9px] text-[#6b7a99]">Pending</p>
                </div>
                <div className="bg-[#0a0e17] rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-cyan-400">{referralData.bonus_days_earned || 0}</p>
                  <p className="text-[9px] text-[#6b7a99]">Days Earned</p>
                </div>
              </div>
              
              {/* How it works */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 mb-3">
                <p className="text-[10px] text-amber-400 font-semibold mb-1">How it works:</p>
                <p className="text-[10px] text-[#6b7a99]">
                  Share your code with friends. When they subscribe to a 1+ month plan, you get <span className="text-green-400 font-bold">7 days FREE</span>!
                </p>
              </div>
              
              {/* Share Buttons */}
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  onClick={copyReferralLink}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy Link
                </Button>
                <Button
                  size="sm"
                  onClick={shareViaWhatsApp}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs"
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  WhatsApp
                </Button>
              </div>
              
              {/* View Full Dashboard Button */}
              <Button
                size="sm"
                onClick={() => {
                  setShowReferralPanel(false);
                  setShowFullDashboard(true);
                }}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs"
                data-testid="view-full-dashboard-btn"
              >
                <BarChart3 className="w-3 h-3 mr-1" />
                View Full Dashboard
              </Button>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-red-400">Failed to load referral data</p>
              <p className="text-xs text-[#6b7a99]">Please try again later</p>
            </div>
          )}
        </div>
      )}
      
      {/* Full Referral Dashboard Modal */}
      <ReferralDashboard 
        isOpen={showFullDashboard}
        onClose={() => setShowFullDashboard(false)}
        user={user}
      />
    </div>
  );
};

// Background Service Status Component with Real-time Connection Indicator
const BackgroundServiceStatus = ({ wsConnected }) => {
  const [status, setStatus] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiClient.get('/background-service/status');
        setStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch service status:', error);
      }
    };
    
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);
  
  if (!status) return null;
  
  const isRunning = status.status === 'running';
  const isReady = status.status === 'ready';
  
  return (
    <div 
      className={`rounded-lg border ${
        isRunning ? 'border-green-500/30 bg-green-900/20' : 
        isReady ? 'border-yellow-500/30 bg-yellow-900/20' : 
        'border-red-500/30 bg-red-900/20'
      } backdrop-blur-sm transition-all duration-300 ${isExpanded ? 'w-72' : 'w-auto'}`}
      data-testid="background-service-widget"
    >
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-2 flex items-center gap-2 text-left"
      >
        {/* Real-time WebSocket Connection Status - Green Dot */}
        <div className="flex items-center gap-1.5">
          <div 
            className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}
            title={wsConnected ? 'Real-time connected' : 'Real-time disconnected'}
            data-testid="realtime-connection-indicator"
          />
          {isRunning ? (
            <Wifi className="w-4 h-4 text-green-400" />
          ) : isReady ? (
            <Server className="w-4 h-4 text-yellow-400" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-400" />
          )}
        </div>
        <span className={`text-xs font-medium ${
          isRunning ? 'text-green-400' : isReady ? 'text-yellow-400' : 'text-red-400'
        }`}>
          {isRunning ? '24/7 Active' : isReady ? 'Ready' : 'Disconnected'}
        </span>
        {isRunning && (
          <span className="text-[10px] text-[#71717A] ml-auto">
            {status.ticks_per_minute}/min
          </span>
        )}
        <ChevronDown className={`w-3 h-3 text-[#71717A] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 text-[10px] border-t border-[#27272A] pt-2">
          <div className="flex justify-between">
            <span className="text-[#71717A]">WebSocket</span>
            <span className={wsConnected ? 'text-green-400' : 'text-red-400'}>
              {wsConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Status</span>
            <span className={isRunning ? 'text-green-400' : isReady ? 'text-yellow-400' : 'text-red-400'}>
              {status.message?.split(' ').slice(1).join(' ')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Uptime</span>
            <span className="text-white">{status.uptime}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Total Ticks</span>
            <span className="text-white">{status.total_ticks_received?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Since Last Save</span>
            <span className="text-white">{status.ticks_since_last_save?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Last Auto-Save</span>
            <span className="text-white">{status.last_auto_save}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Reconnects</span>
            <span className="text-white">{status.reconnect_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Subscribed</span>
            <span className="text-white">{status.subscribed_symbols} symbols</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#71717A]">Buffered Ticks</span>
            <span className="text-white">{status.buffered_ticks?.toLocaleString() || 0}</span>
          </div>
          <div className="text-[9px] text-[#52525B] mt-2 pt-2 border-t border-[#27272A]">
            Backend collects data 24/7. Safe to close browser - no data loss.
          </div>
        </div>
      )}
    </div>
  );
};

// Format number with commas
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return "-";
  return Number(num).toLocaleString('en-IN', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Format large numbers
const formatVolume = (num) => {
  if (num === undefined || num === null) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(2) + " K";
  return num.toLocaleString('en-IN');
};

// Data bar component with gradient depth based on quantity
const DataBar = ({ value, maxValue, color = "blue" }) => {
  const percentage = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  
  // Calculate opacity/intensity based on percentage (0.3 to 1.0)
  const intensity = 0.3 + (percentage / 100) * 0.7;
  
  // Gradient definitions based on color
  const getGradient = () => {
    switch(color) {
      case "green":
        return `linear-gradient(90deg, rgba(34,197,94,${intensity * 0.3}) 0%, rgba(34,197,94,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      case "red":
        return `linear-gradient(90deg, rgba(239,68,68,${intensity * 0.3}) 0%, rgba(239,68,68,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      case "purple":
        return `linear-gradient(90deg, rgba(168,85,247,${intensity * 0.3}) 0%, rgba(168,85,247,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      case "orange":
        return `linear-gradient(90deg, rgba(249,115,22,${intensity * 0.3}) 0%, rgba(249,115,22,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      case "cyan":
        return `linear-gradient(90deg, rgba(6,182,212,${intensity * 0.3}) 0%, rgba(6,182,212,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      case "yellow":
        return `linear-gradient(90deg, rgba(234,179,8,${intensity * 0.3}) 0%, rgba(234,179,8,${intensity}) ${percentage}%, transparent ${percentage}%)`;
      default: // blue
        return `linear-gradient(90deg, rgba(59,130,246,${intensity * 0.3}) 0%, rgba(59,130,246,${intensity}) ${percentage}%, transparent ${percentage}%)`;
    }
  };
  
  return (
    <div 
      className="relative w-full h-5 bg-[#1a1a1a] rounded-sm overflow-hidden"
      style={{ background: `#1a1a1a` }}
    >
      <div 
        className="absolute left-0 top-0 h-full w-full rounded-sm"
        style={{ background: getGradient() }}
      />
      <span className="absolute inset-0 flex items-center justify-center text-xs font-mono text-white drop-shadow-sm">
        {formatVolume(value)}
      </span>
    </div>
  );
};

// Default column visibility
const DEFAULT_COLUMNS = {
  symbol: true,
  ltp: true,
  change: true,
  open: true,
  high: true,
  low: true,
  volume: true,
  vwap: true,
  buyQty: true,
  sellQty: true,
  bid: true,
  ask: true,
  ltq: true,
  oi: true,
  oiChange: true,
  aggBuy: true,
  aggSell: true,
};

// Default column widths (in pixels)
const DEFAULT_COLUMN_WIDTHS = {
  symbol: 130,
  ltp: 90,
  change: 110,
  open: 85,
  high: 85,
  low: 85,
  volume: 85,
  vwap: 85,
  buyQty: 85,
  sellQty: 85,
  bid: 85,
  ask: 85,
  ltq: 70,
  oi: 85,
  oiChange: 85,
  aggBuy: 75,
  aggSell: 75,
};

// Get saved column widths from localStorage
const getSavedColumnWidths = () => {
  try {
    const saved = localStorage.getItem('columnWidths');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_WIDTHS;
  } catch {
    return DEFAULT_COLUMN_WIDTHS;
  }
};

// Resizable Column Header Component
const ResizableHeader = ({ columnKey, label, width, onResize, sortable, onSort, sortField, sortDirection, children }) => {
  const [isResizing, setIsResizing] = useState(false);
  const startX = React.useRef(0);
  const startWidth = React.useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
    
    // Add class to body to prevent text selection during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    const handleMouseMove = (e) => {
      e.preventDefault();
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(50, startWidth.current + diff);
      onResize(columnKey, newWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th 
      className="py-2 px-2 text-left text-[10px] font-semibold text-[#71717A] uppercase border-b border-[#27272A] relative select-none group"
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      data-testid={`column-header-${columnKey}`}
    >
      <div 
        className={`flex items-center gap-1 pr-2 ${sortable ? 'cursor-pointer hover:text-white' : ''}`}
        onClick={sortable ? () => onSort(columnKey) : undefined}
      >
        {label}
        {sortable && sortField === columnKey && (
          <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
        {children}
      </div>
      {/* Resize handle - wider hitbox for easier grabbing */}
      <div
        className={`absolute right-0 top-0 h-full w-3 cursor-col-resize z-20 flex items-center justify-center
          ${isResizing ? 'bg-blue-500/30' : 'hover:bg-blue-500/20'}`}
        onMouseDown={handleMouseDown}
        data-testid={`resize-handle-${columnKey}`}
      >
        <div className={`w-0.5 h-3/5 rounded-full transition-colors
          ${isResizing ? 'bg-blue-500' : 'bg-transparent group-hover:bg-blue-500/50'}`} 
        />
      </div>
    </th>
  );
};

// Ticker Row Component
const TickerRow = ({ tick, instrument, onRemove, maxBuyQty, maxSellQty, maxLtq, maxOi, maxOiChange, visibleColumns, aggStreak, columnWidths }) => {
  const [flash, setFlash] = useState(null);
  const prevPriceRef = React.useRef(tick?.last_price);
  
  // Get symbol name from instrument or tick data - prioritize non-empty values
  const getSymbolName = () => {
    // Check instrument data first (from instruments map)
    if (instrument?.tradingsymbol && instrument.tradingsymbol.trim()) {
      return instrument.tradingsymbol;
    }
    // Then check tick data
    if (tick?.tradingsymbol && tick.tradingsymbol.trim()) {
      return tick.tradingsymbol;
    }
    if (tick?.symbol && tick.symbol.trim()) {
      return tick.symbol;
    }
    // Last resort - show token as "Loading..." if it looks like an instrument token
    const token = tick?.instrument_token;
    if (token && typeof token === 'number') {
      return `Loading... (${token})`;
    }
    return String(token || 'Unknown');
  };
  const symbolName = getSymbolName();

  useEffect(() => {
    if (tick?.last_price !== prevPriceRef.current) {
      setFlash(tick?.last_price > prevPriceRef.current ? 'up' : 'down');
      prevPriceRef.current = tick?.last_price;
      setTimeout(() => setFlash(null), 500);
    }
  }, [tick?.last_price]);

  const change = tick?.change || 0;
  const changePercent = tick?.last_price && tick?.ohlc?.close 
    ? ((tick.last_price - tick.ohlc.close) / tick.ohlc.close * 100) 
    : 0;
  const isPositive = change >= 0;
  
  const isAggBuy = tick?.agg_buy > 0;
  const isAggSell = tick?.agg_sell > 0;
  
  // Strong highlight for consistent agg buy/sell (streak >= 3)
  const hasAggBuyStreak = aggStreak > 0 && aggStreak >= 3;
  const hasAggSellStreak = aggStreak < 0 && aggStreak <= -3;
  
  const rowBgClass = hasAggBuyStreak 
    ? "bg-green-900/40 border-l-4 border-l-green-500" 
    : hasAggSellStreak 
      ? "bg-red-900/40 border-l-4 border-l-red-500"
      : isAggBuy 
        ? "bg-green-900/20" 
        : isAggSell 
          ? "bg-red-900/20" 
          : "";

  return (
    <tr 
      className={`border-b border-[#27272A] hover:bg-[#161616] transition-colors ${rowBgClass} ${
        flash === 'up' ? 'flash-up' : flash === 'down' ? 'flash-down' : ''
      }`}
      data-testid={`ticker-row-${tick?.instrument_token}`}
    >
      {visibleColumns.symbol && (
        <td className="py-1.5 px-2 text-sm font-medium text-white sticky left-0 bg-[#111111] z-10">
          <div className="flex items-center gap-1">
            <span className="truncate min-w-[180px] max-w-[220px]">{symbolName}</span>
            {isPositive ? (
              <TrendingUp className="w-3 h-3 text-green-500 flex-shrink-0" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500 flex-shrink-0" />
            )}
            {/* Streak indicator */}
            {hasAggBuyStreak && <span className="text-[10px] text-green-400 ml-1">x{aggStreak}</span>}
            {hasAggSellStreak && <span className="text-[10px] text-red-400 ml-1">x{Math.abs(aggStreak)}</span>}
          </div>
        </td>
      )}
      {visibleColumns.ltp && (
        <td className={`py-1.5 px-2 font-mono text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
          {formatNumber(tick?.last_price)}
        </td>
      )}
      {visibleColumns.change && (
        <td className={`py-1.5 px-2 font-mono text-xs ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
          {isPositive ? '+' : ''}{formatNumber(change)} ({isPositive ? '+' : ''}{changePercent.toFixed(2)}%)
        </td>
      )}
      {visibleColumns.open && <td className="py-1.5 px-2 font-mono text-xs text-[#A1A1AA]">{formatNumber(tick?.ohlc?.open)}</td>}
      {visibleColumns.high && <td className="py-1.5 px-2 font-mono text-xs text-green-500">{formatNumber(tick?.ohlc?.high)}</td>}
      {visibleColumns.low && <td className="py-1.5 px-2 font-mono text-xs text-red-500">{formatNumber(tick?.ohlc?.low)}</td>}
      {visibleColumns.volume && <td className="py-1.5 px-2 font-mono text-xs text-[#A1A1AA]">{formatVolume(tick?.volume_traded)}</td>}
      {visibleColumns.vwap && <td className="py-1.5 px-2 font-mono text-xs text-[#A1A1AA]">{formatNumber(tick?.average_traded_price)}</td>}
      {visibleColumns.buyQty && (
        <td className="py-1.5 px-2 min-w-[80px]">
          <DataBar value={tick?.total_buy_quantity} maxValue={maxBuyQty} color="green" />
        </td>
      )}
      {visibleColumns.sellQty && (
        <td className="py-1.5 px-2 min-w-[80px]">
          <DataBar value={tick?.total_sell_quantity} maxValue={maxSellQty} color="red" />
        </td>
      )}
      {visibleColumns.bid && <td className="py-1.5 px-2 font-mono text-xs text-green-500">{formatNumber(tick?.bid_price)}</td>}
      {visibleColumns.ask && <td className="py-1.5 px-2 font-mono text-xs text-red-500">{formatNumber(tick?.ask_price)}</td>}
      {visibleColumns.ltq && (
        <td className="py-1.5 px-2 min-w-[70px]">
          <DataBar value={tick?.last_traded_quantity} maxValue={maxLtq} color="blue" />
        </td>
      )}
      {visibleColumns.oi && (
        <td className="py-1.5 px-2 min-w-[70px]">
          <DataBar value={tick?.oi} maxValue={maxOi} color="purple" />
        </td>
      )}
      {visibleColumns.oiChange && (
        <td className="py-1.5 px-2 min-w-[70px]">
          <DataBar 
            value={Math.abs(tick?.oi_change || 0)} 
            maxValue={maxOiChange} 
            color={(tick?.oi_change || 0) >= 0 ? "green" : "red"} 
          />
        </td>
      )}
      {visibleColumns.aggBuy && (
        <td className={`py-1.5 px-2 font-mono text-xs font-bold ${isAggBuy ? 'text-green-400 bg-green-900/40' : 'text-[#3f3f46]'}`}>
          {isAggBuy ? formatVolume(tick?.agg_buy) : "-"}
        </td>
      )}
      {visibleColumns.aggSell && (
        <td className={`py-1.5 px-2 font-mono text-xs font-bold ${isAggSell ? 'text-red-400 bg-red-900/40' : 'text-[#3f3f46]'}`}>
          {isAggSell ? formatVolume(tick?.agg_sell) : "-"}
        </td>
      )}
      <td className="py-1.5 px-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(tick?.instrument_token)}
          className="h-6 w-6 p-0 hover:bg-red-900/20 hover:text-red-500"
          data-testid={`remove-${tick?.instrument_token}`}
        >
          <X className="w-3 h-3" />
        </Button>
      </td>
    </tr>
  );
};

// Search Result Item with type badge
const SearchResultItem = ({ inst, onClick }) => {
  if (!inst || !inst.instrument_token) return null;
  
  const segment = inst.segment || "";
  const instType = inst.instrument_type || "EQ";
  
  // Determine badge color and label based on segment and type
  let badgeColor = "bg-gray-600";
  let badgeLabel = instType;
  
  if (segment.includes("INDICES")) {
    badgeColor = "bg-purple-600";
    badgeLabel = "IDX";
  } else if (instType === "EQ") {
    if (inst.tradingsymbol?.includes("ETF") || inst.name?.includes("ETF")) {
      badgeColor = "bg-cyan-600";
      badgeLabel = "ETF";
    } else {
      badgeColor = "bg-blue-600";
      badgeLabel = "EQ";
    }
  } else if (instType === "FUT" || instType?.includes("FUT")) {
    badgeColor = "bg-yellow-600";
    badgeLabel = "FUT";
  } else if (instType === "CE") {
    badgeColor = "bg-green-600";
    badgeLabel = "CE";
  } else if (instType === "PE") {
    badgeColor = "bg-red-600";
    badgeLabel = "PE";
  }
  
  return (
    <button
      onClick={() => onClick(inst)}
      className="w-full flex items-center justify-between px-4 py-2 hover:bg-[#27272A] text-left border-b border-[#1a1a1a]"
      data-testid={`search-result-${inst.instrument_token}`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-xs px-2 py-0.5 rounded font-medium ${badgeColor} text-white min-w-[36px] text-center`}>
          {badgeLabel}
        </span>
        <div>
          <p className="text-sm font-medium text-white">{inst.tradingsymbol}</p>
          <p className="text-xs text-[#71717A]">
            {inst.exchange} {inst.strike ? `· Strike: ${inst.strike}` : ''} {inst.expiry ? `· ${inst.expiry}` : ''}
          </p>
        </div>
      </div>
      <Plus className="w-4 h-4 text-blue-500" />
    </button>
  );
};

// Alert Item Component
const AlertItem = ({ alert, onDelete, onToggle }) => {
  const typeLabels = {
    price_above: "Price Above",
    price_below: "Price Below",
    volume_spike: "Volume Spike",
    ltq_spike: "LTQ Spike",
    bid_ask_imbalance: "Bid/Ask Imbalance",
    oi_change: "OI Change",
    aggressive_buy: "Aggressive Buy",
    aggressive_sell: "Aggressive Sell",
    price_breakout: "Price Breakout",
    vwap_deviation: "VWAP Deviation"
  };

  return (
    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#27272A]">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-white">{alert.symbol}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${alert.enabled ? 'bg-green-900/30 text-green-400' : 'bg-[#27272A] text-[#52525B]'}`}>
            {alert.enabled ? 'Active' : 'Disabled'}
          </span>
        </div>
        <p className="text-xs text-[#71717A] mt-1">
          {typeLabels[alert.alert_type] || alert.alert_type} @ {formatNumber(alert.threshold)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggle(alert.id)}
          className="h-8 w-8 p-0"
        >
          {alert.enabled ? <Bell className="w-4 h-4 text-green-500" /> : <Bell className="w-4 h-4 text-[#52525B]" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(alert.id)}
          className="h-8 w-8 p-0 hover:text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Column Settings Dropdown
const ColumnSettings = ({ columns, setColumns }) => {
  const columnLabels = {
    symbol: "Symbol",
    ltp: "LTP",
    change: "Change %",
    open: "Open",
    high: "High",
    low: "Low",
    volume: "Volume",
    vwap: "VWAP",
    buyQty: "Buy Qty",
    sellQty: "Sell Qty",
    bid: "Bid",
    ask: "Ask",
    ltq: "LTQ",
    oi: "OI",
    oiChange: "OI Change",
    aggBuy: "Agg Buy",
    aggSell: "Agg Sell"
  };

  const handleToggle = (key) => {
    setColumns(prev => {
      const newCols = { ...prev, [key]: !prev[key] };
      // Save to localStorage
      localStorage.setItem('dashboardColumns', JSON.stringify(newCols));
      return newCols;
    });
  };

  const resetColumns = () => {
    setColumns(DEFAULT_COLUMNS);
    localStorage.setItem('dashboardColumns', JSON.stringify(DEFAULT_COLUMNS));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 px-3 text-[#A1A1AA] hover:text-white" data-testid="column-settings-btn">
          <Settings2 className="w-4 h-4 mr-2" />
          Columns
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-48 bg-[#111111] border-[#27272A]" align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuLabel className="text-[#71717A]">Visible Columns</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#27272A]" />
        {Object.entries(columnLabels).map(([key, label]) => (
          <DropdownMenuCheckboxItem
            key={key}
            checked={columns[key]}
            onCheckedChange={() => handleToggle(key)}
            onSelect={(e) => e.preventDefault()}
            className="text-white cursor-pointer"
          >
            {label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator className="bg-[#27272A]" />
        <div className="px-2 py-1">
          <Button variant="ghost" size="sm" onClick={resetColumns} className="w-full text-xs">
            Reset to Default
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Main Dashboard Component
export default function Dashboard() {
  const { 
    tickData, 
    alerts, 
    triggeredAlerts,
    wsConnected, 
    subscribedTokens,
    marketStatus,
    currentUser,
    hasPremium,
    refreshUser,
    subscribeInstruments,
    unsubscribeInstruments,
    createAlert,
    deleteAlert,
    fetchAlerts,
    handleLogout,
    handleSwitchBroker,
    connectedBroker,
    checkMarketStatus
  } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();
  
  const [instruments, setInstruments] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ instruments: [], grouped: {} });
  const [isSearching, setIsSearching] = useState(false);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [showTickHistory, setShowTickHistory] = useState(false);
  const [selectedTickSymbol, setSelectedTickSymbol] = useState(null); // Auto-select symbol from Activity Log
  const [selectedExchange, setSelectedExchange] = useState("ALL");
  const [tableFilter, setTableFilter] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState("watchlist");
  const [showScannerPanel, setShowScannerPanel] = useState(false);
  const [showAlgoSetup, setShowAlgoSetup] = useState(false);
  const [algoFullScreen, setAlgoFullScreen] = useState(false);
  const [algoHighlightedCells, setAlgoHighlightedCells] = useState({}); // Track highlighted cells from background monitor
  const [showServerAlerts, setShowServerAlerts] = useState(false); // Server-side alert conditions panel
  const [showTelegramSettings, setShowTelegramSettings] = useState(false); // Telegram settings modal
  const [telegramCredentials, setTelegramCredentials] = useState({ bot_token: '', chat_id: '' });
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramSaving, setTelegramSaving] = useState(false);
  
  // Active watchlist view: 'default' (Watchlist with pinned) or 'my_watchlist' (personal)
  const [activeWatchlistView, setActiveWatchlistView] = useState("default");

  // ZERODHA SESSION HEALTH — auto-detect expired access token
  // (UI shows "connected" based on stored token but Zerodha tokens expire daily at 6 AM IST.
  //  Without this, user sees "connected" but no ticks come in. Now we poll backend status
  //  and show a prominent red banner with one-click reconnect if session is dead during market hours.)
  const [sessionHealth, setSessionHealth] = useState({
    websocketConnected: null,  // null = unknown, true/false once known
    sessionValid: null,
    marketOpen: false,
    totalTicks: 0,
    subscribedSymbols: 0,
    lastChecked: null,
  });
  const [bannerDismissedAt, setBannerDismissedAt] = useState(null);

  useEffect(() => {
    if (!connectedBroker) return;
    let stopped = false;
    const fetchHealth = async () => {
      try {
        const r = await apiClient.get('/background-service/status');
        if (stopped) return;
        setSessionHealth({
          websocketConnected: r.data?.websocket_connected ?? false,
          sessionValid: r.data?.session_valid ?? false,
          marketOpen: r.data?.market_open ?? false,
          totalTicks: r.data?.total_ticks_received ?? 0,
          subscribedSymbols: r.data?.subscribed_symbols ?? 0,
          lastChecked: new Date(),
        });
      } catch (_) {
        // ignore - banner stays as-is
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 30000); // every 30s
    return () => { stopped = true; clearInterval(id); };
  }, [connectedBroker]);
  
  // User's actual watchlist tokens (only tokens the user has added, not from server state)
  const [watchlistTokens, setWatchlistTokens] = useState(() => {
    // Try to restore from localStorage as initial state
    try {
      const saved = localStorage.getItem('watchlistTokens');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // My Watchlist tokens (user's personal watchlist - separate from default)
  const [myWatchlistTokens, setMyWatchlistTokens] = useState(() => {
    try {
      const saved = localStorage.getItem('myWatchlistTokens');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [defaultTokens, setDefaultTokens] = useState(() => {
    // Try to restore from localStorage
    const saved = localStorage.getItem('defaultTokens');
    return saved ? JSON.parse(saved) : [];
  }); // Track default 9 pinned tokens to keep at top
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionFeature, setSubscriptionFeature] = useState("");
  
  // Admin approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  
  // Check if user is admin approved (for feature access)
  const isAdminApproved = useMemo(() => {
    if (!currentUser) return false;
    // Default to true for backward compatibility with existing users
    return currentUser.admin_approved !== false;
  }, [currentUser]);
  
  // Function to check approval before accessing features
  const checkApprovalAndProceed = useCallback((callback) => {
    if (!isAdminApproved) {
      setShowApprovalModal(true);
      return false;
    }
    if (callback) callback();
    return true;
  }, [isAdminApproved]);
  
  // New feature states
  const [fontSize, setFontSize] = useState(100); // Zoom percentage
  const [isLiveUpdating, setIsLiveUpdating] = useState(true); // Load on/off
  const [pausedTickData, setPausedTickData] = useState(null); // Store paused data
  
  // Learn More / YouTube link state
  const [youtubeLink, setYoutubeLink] = useState('');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Fetch YouTube link on mount
  useEffect(() => {
    const fetchYoutubeLink = async () => {
      try {
        const response = await apiClient.get('/contact/settings');
        if (response.data?.youtube_channel) {
          setYoutubeLink(response.data.youtube_channel);
        }
      } catch (err) {
        console.error('Failed to fetch YouTube link:', err);
      }
    };
    fetchYoutubeLink();
  }, []);
  
  // Copy YouTube link to clipboard
  const copyYoutubeLink = async () => {
    if (!youtubeLink) return;
    const success = await copyToClipboard(youtubeLink, 'YouTube link copied!');
    if (success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };
  
  // Edit API Credentials Modal State
  const [showEditApiModal, setShowEditApiModal] = useState(false);
  const [apiCredentials, setApiCredentials] = useState({
    api_key: '',
    api_secret: '',
    totp_secret: ''
  });
  const [apiCredentialsLoading, setApiCredentialsLoading] = useState(false);
  
  // Load Telegram settings when modal opens
  useEffect(() => {
    if (showTelegramSettings) {
      const loadTelegramSettings = async () => {
        setTelegramLoading(true);
        try {
          const res = await apiClient.get('/user/telegram-settings');
          if (res.data) {
            setTelegramCredentials({
              bot_token: res.data.bot_token || '',
              chat_id: res.data.chat_id || ''
            });
          }
        } catch (err) {
          console.error('Failed to load telegram settings:', err);
        } finally {
          setTelegramLoading(false);
        }
      };
      loadTelegramSettings();
    }
  }, [showTelegramSettings]);
  
  // Save Telegram settings
  const saveTelegramSettings = async () => {
    setTelegramSaving(true);
    try {
      await apiClient.post('/user/telegram-settings', {
        enabled: true,
        bot_token: telegramCredentials.bot_token,
        chat_id: telegramCredentials.chat_id
      });
      toast.success('Telegram settings saved!', { icon: '📱' });
      setShowTelegramSettings(false);
    } catch (err) {
      toast.error('Failed to save Telegram settings');
      console.error(err);
    } finally {
      setTelegramSaving(false);
    }
  };
  
  // Test Telegram connection
  const testTelegramConnection = async () => {
    if (!telegramCredentials.bot_token || !telegramCredentials.chat_id) {
      toast.error('Please enter both Bot Token and Chat ID');
      return;
    }
    try {
      const res = await apiClient.post('/user/telegram-test', {
        bot_token: telegramCredentials.bot_token,
        chat_id: telegramCredentials.chat_id
      });
      if (res.data?.success) {
        toast.success('Test message sent! Check your Telegram.', { icon: '✅' });
      } else {
        toast.error(res.data?.error || 'Failed to send test message');
      }
    } catch (err) {
      toast.error('Failed to test connection');
    }
  };
  
  // Fetch current API credentials (masked)
  const fetchApiCredentials = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiClient.get('/user/kite-credentials', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setApiCredentials({
          api_key: response.data.api_key || '',
          api_secret: '', // Don't show secret
          totp_secret: '' // Don't show TOTP
        });
      }
    } catch (err) {
      console.error('Failed to fetch API credentials:', err);
    }
  };
  
  // Save API credentials
  const saveApiCredentials = async () => {
    setApiCredentialsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const payload = {
        api_key: apiCredentials.api_key
      };
      if (apiCredentials.api_secret) {
        payload.api_secret = apiCredentials.api_secret;
      }
      if (apiCredentials.totp_secret) {
        payload.totp_secret = apiCredentials.totp_secret;
      }
      
      const response = await apiClient.post('/user/update-kite-credentials', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('API credentials updated successfully!');
        setShowEditApiModal(false);
        // Refresh auth status
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials');
    }
    setApiCredentialsLoading(false);
  };
  
  // Smart Filter Alert History State
  const [showSmartFilterHistory, setShowSmartFilterHistory] = useState(false);
  const [smartFilterAlerts, setSmartFilterAlerts] = useState([]);
  const [smartFilterLoading, setSmartFilterLoading] = useState(false);
  
  // Fetch Smart Filter Alert History
  const fetchSmartFilterAlerts = async () => {
    setSmartFilterLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiClient.get('/user/smart-filter/alerts?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartFilterAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch smart filter alerts:', err);
      toast.error('Failed to load alert history');
    }
    setSmartFilterLoading(false);
  };
  
  // Check premium access for a feature
  const checkPremiumAccess = useCallback((featureName) => {
    if (hasPremium && hasPremium()) {
      return true;
    }
    setSubscriptionFeature(featureName);
    setShowSubscriptionModal(true);
    return false;
  }, [hasPremium]);
  
  // Zerodha Auth Status State
  const [zerodhaAuthStatus, setZerodhaAuthStatus] = useState({
    can_auth: false,
    has_admin_permission: false,
    is_subscribed: false,
    loading: true
  });
  
  // Check Zerodha Auth permission on mount
  useEffect(() => {
    const checkZerodhaAuthStatus = async () => {
      try {
        const response = await apiClient.get('/user/zerodha-auth-status');
        setZerodhaAuthStatus({
          ...response.data,
          loading: false
        });
      } catch (err) {
        console.error('Failed to check Zerodha auth status:', err);
        setZerodhaAuthStatus(prev => ({ ...prev, loading: false }));
      }
    };
    checkZerodhaAuthStatus();
  }, []);
  
  // Handle Zerodha OAuth callback (when user returns from Zerodha login)
  useEffect(() => {
    const handleZerodhaCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const requestToken = urlParams.get('request_token');
      const isKiteCallback = urlParams.get('kite_callback');
      
      if (requestToken && isKiteCallback) {
        // GLOBAL single-use lock: prevent race with ConnectZerodha.jsx (or any duplicate handler)
        // A Zerodha request_token can only be exchanged ONCE - second call returns "Invalid token"
        // which used to force the user to re-enter the app code.
        const claimedKey = `zerodha_token_claimed_${requestToken}`;
        if (sessionStorage.getItem(claimedKey)) {
          // Another handler already exchanged this token - just clean URL and exit
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        sessionStorage.setItem(claimedKey, '1');

        toast.loading("Authenticating with Zerodha...", { id: "zerodha-callback" });
        try {
          const response = await apiClient.post(`/user/kite/callback?request_token=${requestToken}`);
          if (response.data.success) {
            toast.success(`Zerodha authenticated! Welcome ${response.data.kite_user_name || 'User'}`, { id: "zerodha-callback" });
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
            // Reload to refresh connection status
            window.location.reload();
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to authenticate with Zerodha', { id: "zerodha-callback" });
          // Clear URL params
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };
    handleZerodhaCallback();
  }, []);
  
  // One-click Zerodha authentication - direct to Kite login
  const handleOneClickAuth = useCallback(async () => {
    try {
      // Check admin permission and subscription first
      if (!zerodhaAuthStatus.can_auth) {
        if (!zerodhaAuthStatus.has_admin_permission) {
          toast.error("Zerodha Auth not enabled. Contact admin for permission.", { duration: 4000 });
          return;
        }
        if (!zerodhaAuthStatus.is_subscribed) {
          toast.error("Please subscribe to use Zerodha authentication.", { duration: 4000 });
          setShowSubscriptionModal(true);
          return;
        }
      }
      
      toast.loading("Getting Zerodha login URL...", { id: "zerodha-auth" });
      const response = await apiClient.get('/auth/zerodha-login-url');
      const { login_url, credentials_source, has_saved_credentials, requires_setup } = response.data;
      
      // SECURITY: If user has no credentials, they must set up their own
      if (requires_setup || !login_url) {
        toast.error("Please set up your Zerodha API credentials first. Go to Settings > Zerodha Credentials.", { 
          id: "zerodha-auth", 
          duration: 5000 
        });
        return;
      }
      
      if (has_saved_credentials) {
        toast.success("Using your saved API credentials", { id: "zerodha-auth", duration: 2000 });
      } else {
        toast.dismiss("zerodha-auth");
      }
      
      // Store current URL for redirect after auth
      localStorage.setItem('pre_auth_path', window.location.pathname);
      
      // Redirect to Zerodha login
      window.location.href = login_url;
    } catch (error) {
      console.error("Failed to get Zerodha login URL:", error);
      toast.error("Failed to connect. Please try again.", { id: "zerodha-auth" });
    }
  }, [zerodhaAuthStatus]);
  
  // Handle navigation from Activity Log with selected symbol
  useEffect(() => {
    if (location.state?.openTickHistory) {
      setShowTickHistory(true);
      if (location.state?.selectedSymbol) {
        setSelectedTickSymbol(location.state.selectedSymbol);
      }
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Column visibility state - load from localStorage
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('dashboardColumns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  
  // Column widths state - resizable columns
  const [columnWidths, setColumnWidths] = useState(getSavedColumnWidths);
  
  // Handle column resize
  const handleColumnResize = useCallback((columnKey, newWidth) => {
    setColumnWidths(prev => {
      const updated = { ...prev, [columnKey]: newWidth };
      localStorage.setItem('columnWidths', JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  // Reset column widths to default
  const resetColumnWidths = useCallback(() => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    localStorage.setItem('columnWidths', JSON.stringify(DEFAULT_COLUMN_WIDTHS));
    toast.success("Column widths reset to default");
  }, []);
  
  const [newAlert, setNewAlert] = useState({
    instrument_token: null,
    symbol: "",
    alert_type: "price_above",
    threshold: 0
  });
  
  // Handle live update toggle
  const toggleLiveUpdate = useCallback(() => {
    if (isLiveUpdating) {
      // Pausing - save current data
      setPausedTickData({ ...tickData });
      toast.info("Live updates paused");
    } else {
      // Resuming - clear paused data
      setPausedTickData(null);
      toast.success("Live updates resumed");
    }
    setIsLiveUpdating(prev => !prev);
  }, [isLiveUpdating, tickData]);
  
  // Get tick data (live or paused)
  // Also show subscribed tokens even without tick data (as placeholders)
  // Filter based on active watchlist view
  const displayTickData = useMemo(() => {
    const liveTicks = isLiveUpdating ? tickData : (pausedTickData || tickData);
    
    // Merge live ticks with placeholder entries for watchlist tokens without data
    const mergedTicks = { ...liveTicks };
    
    // Get active tokens based on which watchlist is being viewed
    const activeTokens = activeWatchlistView === 'my_watchlist' ? myWatchlistTokens : watchlistTokens;
    
    // Create placeholder entries for active watchlist tokens that don't have tick data yet
    activeTokens.forEach(token => {
      if (!mergedTicks[token]) {
        const instrument = instruments[token];
        if (instrument) {
          mergedTicks[token] = {
            instrument_token: token,
            tradingsymbol: instrument.tradingsymbol || instrument.trading_symbol || `Token ${token}`,
            last_price: 0,
            volume: 0,
            ohlc: { open: 0, high: 0, low: 0, close: 0 },
            total_buy_quantity: 0,
            total_sell_quantity: 0,
            change: 0,
            change_percent: 0,
            oi: 0,
            oi_change: 0,
            last_traded_quantity: 0,
            agg_buy: 0,
            agg_sell: 0,
            _placeholder: true  // Mark as placeholder
          };
        }
      }
    });
    
    // Filter to only show tokens from the active watchlist
    const filteredTicks = {};
    activeTokens.forEach(token => {
      if (mergedTicks[token]) {
        filteredTicks[token] = mergedTicks[token];
      }
    });
    
    return filteredTicks;
  }, [isLiveUpdating, tickData, pausedTickData, watchlistTokens, myWatchlistTokens, activeWatchlistView, instruments]);

  // Fetch instruments - Zerodha only
  const fetchInstruments = useCallback(async (exchange = "NSE") => {
    try {
      // Use Zerodha instruments endpoint
      const response = await apiClient.get(`/instruments?exchange=${exchange}`);
      const instMap = {};
      (response.data.instruments || []).forEach(i => {
        instMap[i.instrument_token] = i;
      });
      setInstruments(prev => ({ ...prev, ...instMap }));
      toast.success(`Loaded ${response.data.count} ${exchange} instruments`);
    } catch (error) {
      console.error(`Failed to fetch ${exchange} instruments:`, error);
      toast.error(`Failed to fetch ${exchange} instruments`);
    }
  }, []);

  // Search instruments - Zerodha only
  const searchInstruments = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ instruments: [], grouped: {} });
      return;
    }

    setIsSearching(true);
    try {
      const exchange = selectedExchange === "ALL" ? null : selectedExchange;
      // Use Zerodha search endpoint
      const response = await apiClient.post("/instruments/search", {
        query,
        exchange
      });
      console.log("Search results:", response.data);
      if (response.data && response.data.instruments) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults({ instruments: [], grouped: {} });
    } finally {
      setIsSearching(false);
    }
  }, [selectedExchange]);

  // Debounced search - 100ms for fast response
  useEffect(() => {
    const timer = setTimeout(() => {
      searchInstruments(searchQuery);
    }, 10);
    return () => clearTimeout(timer);
  }, [searchQuery, searchInstruments]);

  // Subscribe to instrument - adds to the currently active watchlist
  const handleSubscribe = async (instrument) => {
    const token = instrument?.instrument_token;
    const symbol = instrument?.tradingsymbol || instrument?.trading_symbol;
    
    if (!instrument || !token) {
      console.error("Invalid instrument:", instrument);
      toast.error("Invalid instrument selected");
      return;
    }
    
    // Check max limit for My Watchlist
    if (activeWatchlistView === 'my_watchlist' && myWatchlistTokens.length >= 150) {
      toast.error("My Watchlist limit reached (150 symbols max)");
      return;
    }
    
    try {
      await subscribeInstruments([parseInt(token)]);
      
      // Normalize instrument data for watchlist
      const normalizedInstrument = {
        ...instrument,
        instrument_token: token,
        tradingsymbol: symbol
      };
      
      setInstruments(prev => ({
        ...prev,
        [token]: normalizedInstrument
      }));
      
      // Add to the active watchlist
      if (activeWatchlistView === 'my_watchlist') {
        setMyWatchlistTokens(prev => [...new Set([...prev, token])]);
        toast.success(`Added ${symbol} to My Watchlist`);
      } else {
        setWatchlistTokens(prev => [...new Set([...prev, token])]);
        toast.success(`Added ${symbol}`);
      }
      
      setSearchQuery("");
      setSearchResults({ instruments: [], grouped: {} });
    } catch (error) {
      console.error("Subscribe failed:", error);
      toast.error("Failed to subscribe: " + (error.response?.data?.detail || error.message));
    }
  };

  // Unsubscribe from instrument - removes from the currently active watchlist
  const handleUnsubscribe = async (token) => {
    try {
      await unsubscribeInstruments([token]);
      
      // Remove from the active watchlist
      if (activeWatchlistView === 'my_watchlist') {
        setMyWatchlistTokens(prev => prev.filter(t => t !== token));
      } else {
        setWatchlistTokens(prev => prev.filter(t => t !== token));
      }
    } catch (error) {
      console.error("Unsubscribe failed:", error);
    }
  };

  // Create alert
  const handleCreateAlert = async () => {
    if (!newAlert.instrument_token || !newAlert.threshold) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      await createAlert(newAlert);
      setNewAlert({ instrument_token: null, symbol: "", alert_type: "price_above", threshold: 0 });
    } catch (error) {
      console.error("Create alert failed:", error);
    }
  };

  // Toggle alert
  const handleToggleAlert = async (alertId) => {
    try {
      await apiClient.patch(`/alerts/${alertId}/toggle`);
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to toggle alert");
    }
  };

  // Load instruments on mount
  useEffect(() => {
    fetchInstruments("NSE");
    fetchInstruments("NFO");
  }, [fetchInstruments]);

  // Load default futures and saved watchlist on mount
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Clear removed tokens tracking on fresh start to avoid blocking legitimate symbols
  // Only keep removed tokens if they were explicitly removed in the current session
  const [removedTokens, setRemovedTokens] = useState(() => new Set());
  
  // Pinned symbols - always at top, auto-update on expiry
  const [pinnedSymbols, setPinnedSymbols] = useState([]);
  
  // Check if a symbol is expired (has 0 data and likely expired)
  const isSymbolExpired = useCallback((token, tickData) => {
    const tick = tickData[token];
    if (!tick) return false;
    
    // If LTP is 0 and no volume, likely expired
    if (tick.last_price === 0 && (tick.volume === 0 || tick.volume === undefined)) {
      return true;
    }
    return false;
  }, []);
  
  useEffect(() => {
    const loadPinnedAndWatchlist = async () => {
      try {
        // Step 1: Load pinned symbols (always at top) - PARALLEL load for speed
        console.log("[Watchlist] Loading pinned symbols and watchlists in parallel...");
        
        const [pinnedRes, defaultWatchlistRes, myWatchlistRes] = await Promise.all([
          apiClient.get('/pinned-symbols'),
          apiClient.get(`/watchlist?broker=${connectedBroker || 'zerodha'}&watchlist_type=default`),
          apiClient.get(`/watchlist?broker=${connectedBroker || 'zerodha'}&watchlist_type=my_watchlist`)
        ]);
        
        const pinned = pinnedRes.data.pinned || [];
        setPinnedSymbols(pinned);
        console.log(`[Watchlist] Loaded ${pinned.length} pinned symbols`);
        
        // Add pinned symbols to instruments map
        const pinnedInstMap = {};
        const pinnedTokens = [];
        pinned.forEach(p => {
          pinnedInstMap[p.instrument_token] = {
            ...p,
            is_pinned: true
          };
          pinnedTokens.push(p.instrument_token);
        });
        setInstruments(prev => ({ ...prev, ...pinnedInstMap }));
        
        // Process default watchlist
        let savedTokens = defaultWatchlistRes.data.tokens || [];
        let savedSymbols = defaultWatchlistRes.data.symbols || [];

        console.log(`[Watchlist] Loaded default watchlist:`, savedTokens.length, "symbols");

        // ONE-TIME MIGRATION: strip ex-pinned futures that were auto-seeded into
        // saved default watchlists by older code. Now only NIFTY is a default;
        // SENSEX/BANKNIFTY/MIDCPNIFTY/FINNIFTY/GOLD/SILVER/CRUDEOIL/NATURALGAS
        // should NOT persist as saved defaults. User can re-add any of them manually.
        const EX_DEFAULT_FUTURE_NAMES = [
          'BANKNIFTY', 'MIDCPNIFTY', 'FINNIFTY',
          'SENSEX', 'SENSEX50', 'BANKEX',
          'GOLD', 'GOLDM', 'GOLDPETAL',
          'SILVER', 'SILVERM', 'SILVERMIC',
          'CRUDEOIL', 'CRUDEOILM',
          'NATURALGAS', 'NATGASMINI',
        ];
        const isExDefaultFuture = (sym) => {
          if (!sym) return false;
          const s = String(sym).toUpperCase();
          // Match e.g. SENSEX26JUNFUT, BANKNIFTY26JULFUT, GOLD26JUNFUT
          return EX_DEFAULT_FUTURE_NAMES.some(prefix =>
            new RegExp(`^${prefix}\\d{2}[A-Z]{3}FUT$`).test(s)
          );
        };
        const cleanedTokens = [];
        const cleanedSymbols = [];
        let removedCount = 0;
        for (let i = 0; i < savedTokens.length; i++) {
          const sym = savedSymbols[i] || '';
          if (isExDefaultFuture(sym)) {
            removedCount++;
            console.log(`[Watchlist] Auto-removing ex-default future: ${sym}`);
            continue;
          }
          cleanedTokens.push(savedTokens[i]);
          cleanedSymbols.push(sym);
        }
        if (removedCount > 0) {
          savedTokens = cleanedTokens;
          savedSymbols = cleanedSymbols;
          // Persist the cleaned list so it doesn't come back on next reload
          try {
            await apiClient.post('/watchlist', {
              tokens: cleanedTokens,
              symbols: cleanedSymbols,
              broker: connectedBroker || 'zerodha',
              watchlist_type: 'default',
            });
            console.log(`[Watchlist] Cleaned & persisted: removed ${removedCount} ex-default future(s) from default watchlist`);
          } catch (saveErr) {
            console.warn('[Watchlist] Failed to persist cleaned default watchlist:', saveErr);
          }
        }

        // Filter out expired/invalid symbols and pinned symbols (they're already loaded)
        const pinnedTokenSet = new Set(pinnedTokens);
        const validUserTokens = [];
        const instMap = {};
        
        savedSymbols.forEach((sym, idx) => {
          const token = savedTokens[idx];
          if (token && !pinnedTokenSet.has(token)) {
            if (sym && sym.trim() && !/^\d+$/.test(sym.trim())) {
              const symbolStr = sym.trim().toUpperCase();
              // Check for expired symbols - only filter if year is definitely in the past
              const currentYear = new Date().getFullYear() % 100; // Get last 2 digits (26 for 2026)
              const expiryMatch = symbolStr.match(/(\d{2})([A-Z]{3})/);
              if (expiryMatch) {
                const yearDigits = parseInt(expiryMatch[1]);
                // Skip if year is in the past (e.g., 24, 25 when current is 26)
                if (yearDigits < currentYear) {
                  console.log(`[Watchlist] Skipping expired symbol: ${symbolStr} (year ${yearDigits})`);
                  return; // Skip expired
                }
              }
              instMap[token] = { tradingsymbol: sym, instrument_token: token };
              validUserTokens.push(token);
            }
          }
        });
        
        // Process My Watchlist
        const myTokens = myWatchlistRes.data.tokens || [];
        const mySymbols = myWatchlistRes.data.symbols || [];
        const validMyTokens = [];
        
        mySymbols.forEach((sym, idx) => {
          const token = myTokens[idx];
          if (token && sym && sym.trim() && !/^\d+$/.test(sym.trim())) {
            instMap[token] = { tradingsymbol: sym, instrument_token: token };
            validMyTokens.push(token);
          }
        });
        
        setInstruments(prev => ({ ...prev, ...instMap }));
        
        // Set both watchlists
        const allDefaultTokens = [...pinnedTokens, ...validUserTokens];
        setWatchlistTokens(allDefaultTokens);
        setMyWatchlistTokens(validMyTokens);
        localStorage.setItem('watchlistTokens', JSON.stringify(allDefaultTokens));
        localStorage.setItem('myWatchlistTokens', JSON.stringify(validMyTokens));
        
        // Store pinned tokens as default tokens
        setDefaultTokens(pinnedTokens);
        localStorage.setItem('defaultTokens', JSON.stringify(pinnedTokens));
        
        // Subscribe to ALL tokens from both watchlists at once for speed
        const allTokensToSubscribe = [...new Set([...pinnedTokens, ...validUserTokens, ...validMyTokens])];
        if (allTokensToSubscribe.length > 0) {
          await subscribeInstruments(allTokensToSubscribe);
          console.log(`[Watchlist] Subscribed to ${allTokensToSubscribe.length} total symbols`);
        }
        
        toast.success(`Loaded ${allDefaultTokens.length} watchlist + ${validMyTokens.length} my watchlist symbols`);
        
        // Mark initial load as complete
        setTimeout(() => setIsInitialLoadComplete(true), 300);
        
      } catch (error) {
        console.error("Failed to load watchlist:", error);
        setIsInitialLoadComplete(true);
        
        // Fallback: try to load just default futures
        try {
          const defaultFuturesRes = await apiClient.get('/default-futures?force_refresh=true');
          const defaultFutures = defaultFuturesRes.data.futures || [];
          if (defaultFutures.length > 0) {
            const instMap = {};
            const tokens = [];
            defaultFutures.forEach(f => {
              instMap[f.instrument_token] = f;
              tokens.push(f.instrument_token);
            });
            setInstruments(prev => ({ ...prev, ...instMap }));
            setWatchlistTokens(tokens);
            setDefaultTokens(tokens);
            await subscribeInstruments(tokens);
            toast.info(`Loaded ${tokens.length} default symbols`);
          }
        } catch (fallbackError) {
          console.error("Fallback load also failed:", fallbackError);
        }
      }
    };
    
    loadPinnedAndWatchlist();
  }, [subscribeInstruments, connectedBroker]);

  // Save watchlist when watchlist tokens change (only after initial load)
  useEffect(() => {
    if (!isInitialLoadComplete) return;
    
    const saveWatchlist = async () => {
      if (watchlistTokens.length === 0) return;
      
      try {
        const tokensToSave = [];
        const symbolsToSave = [];
        
        watchlistTokens.forEach(t => {
          const inst = instruments[t];
          const symbolName = inst?.tradingsymbol || inst?.trading_symbol;
          // Always save the token, even if we don't have symbol name yet
          tokensToSave.push(t);
          // Save symbol name if available
          if (symbolName && symbolName.trim() && !/^\d+$/.test(symbolName.trim())) {
            symbolsToSave.push(symbolName);
          } else {
            // Try to get from instrument data or use placeholder
            symbolsToSave.push(inst?.name || `Token_${t}`);
          }
        });
        
        await apiClient.post("/watchlist", {
          tokens: tokensToSave,
          symbols: symbolsToSave,
          broker: connectedBroker || 'zerodha',
          watchlist_type: 'default'
        });
        
        localStorage.setItem('watchlistTokens', JSON.stringify(tokensToSave));
        console.log(`[Watchlist] Saved ${tokensToSave.length} default watchlist tokens`);
      } catch (error) {
        console.error("[Watchlist] Failed to save:", error);
      }
    };
    
    const timer = setTimeout(saveWatchlist, 800);
    return () => clearTimeout(timer);
  }, [watchlistTokens, instruments, isInitialLoadComplete, connectedBroker]);

  // Save My Watchlist when tokens change
  useEffect(() => {
    if (!isInitialLoadComplete) return;
    
    const saveMyWatchlist = async () => {
      try {
        const tokensToSave = [];
        const symbolsToSave = [];
        
        myWatchlistTokens.forEach(t => {
          const inst = instruments[t];
          const symbolName = inst?.tradingsymbol || inst?.trading_symbol;
          // Always save the token
          tokensToSave.push(t);
          // Save symbol name if available
          if (symbolName && symbolName.trim() && !/^\d+$/.test(symbolName.trim())) {
            symbolsToSave.push(symbolName);
          } else {
            symbolsToSave.push(inst?.name || `Token_${t}`);
          }
        });
        
        await apiClient.post("/watchlist", {
          tokens: tokensToSave,
          symbols: symbolsToSave,
          broker: connectedBroker || 'zerodha',
          watchlist_type: 'my_watchlist'
        });
        
        localStorage.setItem('myWatchlistTokens', JSON.stringify(tokensToSave));
        console.log(`[Watchlist] Saved ${tokensToSave.length} my_watchlist tokens`);
      } catch (error) {
        console.error("[My Watchlist] Failed to save:", error);
      }
    };
    
    const timer = setTimeout(saveMyWatchlist, 800);
    return () => clearTimeout(timer);
  }, [myWatchlistTokens, instruments, isInitialLoadComplete, connectedBroker]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showTickHistory) {
        setShowTickHistory(false);
      }
      if (e.key === 't' && e.ctrlKey) {
        e.preventDefault();
        setShowTickHistory(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTickHistory]);

  // === UTILITY BUTTONS FOR TROUBLESHOOTING ===
  const [isRefreshingInstruments, setIsRefreshingInstruments] = useState(false);
  const [isReconnectingWS, setIsReconnectingWS] = useState(false);
  
  // Refresh/Reload Instruments - fixes search issues
  const handleRefreshInstruments = async () => {
    setIsRefreshingInstruments(true);
    try {
      toast.info('Refreshing ALL instruments (NSE, BSE, NFO, BFO, MCX, CDS, NCO)...');

      // Clear local cache so frontend re-fetches symbols
      localStorage.removeItem('instruments_cache');

      // Trigger full refresh from Zerodha's public CSV - covers ALL 9 exchanges,
      // works without Kite session, runs in background to avoid Cloudflare timeouts.
      const startResp = await apiClient.post('/instruments/refresh-csv');
      if (!startResp.data?.success) {
        throw new Error(startResp.data?.message || 'Could not start refresh');
      }

      // Poll status every 5 seconds (up to 90s) so the user sees progress
      const initialStatus = await apiClient.get('/instruments/status');
      const startedAt = initialStatus.data?.last_refresh;
      let finalTotal = initialStatus.data?.total || 0;

      for (let i = 0; i < 18; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const s = await apiClient.get('/instruments/status');
          finalTotal = s.data?.total || finalTotal;
          // Refresh completes when last_refresh timestamp changes
          if (s.data?.last_refresh && s.data.last_refresh !== startedAt) {
            const byExchange = s.data.by_exchange || {};
            const nonEmptyExchanges = Object.entries(byExchange).filter(([_, v]) => v > 0).length;
            toast.success(`Loaded ${finalTotal.toLocaleString()} instruments across ${nonEmptyExchanges} exchanges. Search ready!`);
            return;
          }
        } catch (_) {
          // ignore intermittent status errors
        }
      }
      // If we reach here, refresh likely still running but not done
      toast.warning(`Refresh running in background. Currently ${finalTotal.toLocaleString()} loaded. Try search in a few seconds.`);
    } catch (error) {
      console.error('Failed to refresh instruments:', error);
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to refresh instruments. Try again.');
    }
    setIsRefreshingInstruments(false);
  };
  
  // Reconnect WebSocket - fixes tick update issues
  const handleReconnectWebSocket = async () => {
    setIsReconnectingWS(true);
    try {
      toast.info('Reconnecting WebSocket...');
      
      // Call the reconnect API
      const response = await apiClient.post('/user/kite/reconnect');
      
      if (response.data?.success) {
        toast.success('WebSocket reconnected! Ticks should update now.');
      } else {
        toast.warning(response.data?.message || 'Reconnection initiated. Please wait...');
      }
    } catch (error) {
      console.error('Failed to reconnect WebSocket:', error);
      // Try alternative - re-subscribe to current tokens
      try {
        const tokens = [...watchlistTokens, ...myWatchlistTokens];
        if (tokens.length > 0) {
          await apiClient.post('/subscribe', { instrument_tokens: tokens });
          toast.success('Re-subscribed to symbols. Ticks should update now.');
        }
      } catch (e) {
        toast.error('Failed to reconnect. Please refresh the page.');
      }
    }
    setIsReconnectingWS(false);
  };

  // Get ticks as array and filter/sort - keep default 4 at top
  const ticksArray = useMemo(() => {
    let ticks = Object.values(displayTickData);
    
    // Apply filter
    if (tableFilter) {
      const filter = tableFilter.toUpperCase();
      ticks = ticks.filter(tick => {
        const symbol = instruments[tick.instrument_token]?.tradingsymbol || '';
        return symbol.toUpperCase().includes(filter);
      });
    }
    
    // Sort: default tokens at top, then others
    // If no manual sort is applied, keep default order (defaults first, others after)
    if (sortField) {
      ticks.sort((a, b) => {
        let aVal = a[sortField] || 0;
        let bVal = b[sortField] || 0;
        if (sortDirection === "asc") return aVal - bVal;
        return bVal - aVal;
      });
    } else {
      // Default sorting: default tokens first (NIFTY, BANKNIFTY, FINNIFTY, MIDCPNIFTY)
      ticks.sort((a, b) => {
        const aIsDefault = defaultTokens.includes(a.instrument_token);
        const bIsDefault = defaultTokens.includes(b.instrument_token);
        
        if (aIsDefault && !bIsDefault) return -1; // a comes first
        if (!aIsDefault && bIsDefault) return 1;  // b comes first
        
        // If both are default, maintain their original order
        if (aIsDefault && bIsDefault) {
          return defaultTokens.indexOf(a.instrument_token) - defaultTokens.indexOf(b.instrument_token);
        }
        
        // If neither are default, maintain insertion order (by comparing tokens as fallback)
        return 0;
      });
    }
    
    return ticks;
  }, [displayTickData, tableFilter, sortField, sortDirection, instruments, defaultTokens]);

  const maxBuyQty = Math.max(...ticksArray.map(t => t.total_buy_quantity || 0), 1);
  const maxSellQty = Math.max(...ticksArray.map(t => t.total_sell_quantity || 0), 1);
  const maxLtq = Math.max(...ticksArray.map(t => t.last_traded_quantity || 0), 1);
  const maxOi = Math.max(...ticksArray.map(t => t.oi || 0), 1);
  const maxOiChange = Math.max(...ticksArray.map(t => Math.abs(t.oi_change || 0)), 1);

  // Track aggressive order streaks per symbol
  const [aggStreaks, setAggStreaks] = useState({});
  
  useEffect(() => {
    setAggStreaks(prev => {
      const newStreaks = { ...prev };
      ticksArray.forEach(tick => {
        const token = tick.instrument_token;
        const isAggBuy = tick.agg_buy > 0;
        const isAggSell = tick.agg_sell > 0;
        const currentStreak = newStreaks[token] || 0;
        
        if (isAggBuy) {
          // Increase positive streak
          newStreaks[token] = currentStreak > 0 ? currentStreak + 1 : 1;
        } else if (isAggSell) {
          // Increase negative streak
          newStreaks[token] = currentStreak < 0 ? currentStreak - 1 : -1;
        }
        // If neither, keep current streak (don't reset immediately)
      });
      return newStreaks;
    });
  }, [ticksArray]);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Alert types for dropdown
  const alertTypes = [
    { value: "price_above", label: "Price Above" },
    { value: "price_below", label: "Price Below" },
    { value: "volume_spike", label: "Volume Spike" },
    { value: "ltq_spike", label: "LTQ Spike" },
    { value: "bid_ask_imbalance", label: "Bid/Ask Imbalance (%)" },
    { value: "oi_change", label: "OI Change" },
    { value: "aggressive_buy", label: "Aggressive Buy Qty" },
    { value: "aggressive_sell", label: "Aggressive Sell Qty" },
    { value: "price_breakout", label: "Price Breakout (%)" },
    { value: "vwap_deviation", label: "VWAP Deviation (%)" },
  ];

  return (
    <div className="h-screen w-full flex flex-col bg-[#0A0A0A]" data-testid="dashboard" style={{ fontSize: `${fontSize}%` }}>
      {/* SESSION HEALTH BANNER — shows when Zerodha session is dead during market hours */}
      {(() => {
        const dismissedRecently = bannerDismissedAt && (Date.now() - bannerDismissedAt) < 60 * 60 * 1000;
        const isDead = sessionHealth.marketOpen
                       && sessionHealth.websocketConnected === false
                       && sessionHealth.lastChecked !== null;
        if (!isDead || dismissedRecently) return null;
        return (
          <div className="bg-gradient-to-r from-red-700 to-red-600 text-white px-4 py-2 flex items-center gap-3 shadow-lg border-b-2 border-red-900 animate-pulse" data-testid="session-expired-banner">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1 text-xs sm:text-sm">
              <strong>Zerodha session expired</strong> — ticks not flowing. Zerodha tokens expire daily at 6 AM IST.
              {sessionHealth.subscribedSymbols > 0 && <span className="opacity-80 ml-1">({sessionHealth.subscribedSymbols} symbols subscribed but receiving 0 ticks/min)</span>}
            </div>
            <button
              onClick={async () => {
                try {
                  // Get fresh Kite login URL and navigate
                  const r = await apiClient.get('/user/kite/login-url');
                  if (r.data?.login_url) {
                    window.location.href = r.data.login_url;
                  } else {
                    toast.error('Could not generate Zerodha login URL');
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to start reconnect');
                }
              }}
              className="px-3 py-1 bg-white text-red-700 font-semibold rounded text-xs hover:bg-red-50 transition-colors shadow"
              data-testid="banner-reconnect-zerodha-btn"
            >
              Reconnect Zerodha
            </button>
            <button
              onClick={() => setBannerDismissedAt(Date.now())}
              className="text-white/80 hover:text-white p-1"
              title="Hide for 1 hour"
              data-testid="banner-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })()}
      {/* Bottom-left Widgets Container - Background Service Status & Learn More side by side */}
      <div className="fixed bottom-16 left-4 z-40 flex items-end gap-2">
        <BackgroundServiceStatus wsConnected={wsConnected} />
        
        {/* Learn More Button - Side by side with Service Status */}
        {youtubeLink && (
          <div className="relative">
            <button
              onClick={() => setShowLearnMore(!showLearnMore)}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg shadow-lg shadow-red-500/25 transition-all duration-200 text-xs font-medium"
              data-testid="learn-more-btn"
            >
              <Youtube className="w-4 h-4" />
              Learn More
            </button>
            
            {/* Dropdown with YouTube link */}
            {showLearnMore && (
              <div className="absolute bottom-full left-0 mb-2 w-72 bg-[#111827] border border-[#1a2540] rounded-xl shadow-2xl p-4 z-50">
                <div className="flex items-center gap-2 mb-3">
                  <Youtube className="w-5 h-5 text-red-500" />
                  <h4 className="text-sm font-semibold text-white">YouTube Channel</h4>
                  <button 
                    onClick={() => setShowLearnMore(false)}
                    className="ml-auto text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <p className="text-xs text-[#6b7a99] mb-3">Watch tutorials, trading strategies, and market analysis</p>
                
                <div className="bg-[#0a0e17] rounded-lg p-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-xs text-cyan-400 truncate flex-1">{youtubeLink}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(youtubeLink, '_blank')}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
                    data-testid="open-youtube-btn"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open Channel
                  </button>
                  <button
                    onClick={copyYoutubeLink}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-[#1a2540] hover:bg-[#2a3a60] text-white rounded-lg text-xs font-medium transition-colors"
                    data-testid="copy-youtube-link-btn"
                  >
                    {linkCopied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Tick History Modal */}
      {showTickHistory && (
        <TickHistory 
          instruments={instruments}
          subscribedTokens={watchlistTokens}
          myWatchlistTokens={myWatchlistTokens}
          tickData={displayTickData}
          marketStatus={marketStatus}
          unsubscribeInstruments={unsubscribeInstruments}
          subscribeInstruments={subscribeInstruments}
          onClose={() => {
            setShowTickHistory(false);
            setSelectedTickSymbol(null);
          }}
          preSelectedSymbol={selectedTickSymbol}
        />
      )}

      {/* Header - Two Row Layout for Better Responsiveness */}
      <header className="border-b border-[#27272A] bg-[#111111] flex-shrink-0">
        {/* Row 1: Logo, Status, Search, Core Actions */}
        <div className="h-12 flex items-center justify-between px-4 border-b border-[#222]">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <span className="font-bold text-sm text-white tracking-tight hidden sm:block">AlphaTradeVission Ticks</span>
            </div>
            
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[#0a0a0a] rounded border border-[#333]">
              <div className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-500' : isLiveUpdating ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-[10px] text-[#71717A]">
                {!isLiveUpdating ? 'Paused' : wsConnected ? 'Live' : 'Polling'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Exchange Filter */}
            <Select value={selectedExchange} onValueChange={setSelectedExchange}>
              <SelectTrigger className="w-24 h-8 bg-[#0A0A0A] border-[#27272A] text-white text-xs" data-testid="exchange-filter-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" data-testid="exchange-opt-ALL">ALL</SelectItem>
                <SelectItem value="NSE" data-testid="exchange-opt-NSE">NSE</SelectItem>
                <SelectItem value="NFO" data-testid="exchange-opt-NFO">NFO</SelectItem>
                <SelectItem value="BSE" data-testid="exchange-opt-BSE">BSE</SelectItem>
                <SelectItem value="BFO" data-testid="exchange-opt-BFO">BFO</SelectItem>
                <SelectItem value="MCX" data-testid="exchange-opt-MCX">MCX</SelectItem>
                <SelectItem value="CDS" data-testid="exchange-opt-CDS">CDS</SelectItem>
                <SelectItem value="NCO" data-testid="exchange-opt-NCO">NCO</SelectItem>
                <SelectItem value="NSEIX" data-testid="exchange-opt-NSEIX">NSEIX</SelectItem>
                <SelectItem value="GLOBAL" data-testid="exchange-opt-GLOBAL">GLOBAL</SelectItem>
              </SelectContent>
            </Select>

            {/* Global Search */}
            <GlobalSearch
              onSelect={handleSubscribe}
              placeholder="Search: NIFTY, 25600, CE..."
              connectedBroker={connectedBroker}
              className="w-64 lg:w-80"
              showExamples={false}
              allowMultiple={true}
              data-testid="symbol-search"
            />
            
            {/* Quick Fix Buttons */}
            <div className="flex items-center gap-1">
              {/* Refresh Instruments - fixes search issues */}
              <button
                onClick={handleRefreshInstruments}
                disabled={isRefreshingInstruments}
                className="flex items-center gap-1 px-2 py-1.5 bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/30 rounded text-[9px] text-blue-300 transition-colors"
                title="Refresh Instruments - Fix search issues"
                data-testid="refresh-instruments-btn"
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshingInstruments ? 'animate-spin' : ''}`} />
                {isRefreshingInstruments ? 'Loading...' : 'Fix Search'}
              </button>
              
              {/* Reconnect WebSocket - fixes tick issues */}
              <button
                onClick={handleReconnectWebSocket}
                disabled={isReconnectingWS}
                className="flex items-center gap-1 px-2 py-1.5 bg-green-600/20 hover:bg-green-600/40 border border-green-500/30 rounded text-[9px] text-green-300 transition-colors"
                title="Reconnect WebSocket - Fix tick updates"
                data-testid="reconnect-ws-btn"
              >
                <Wifi className={`w-3 h-3 ${isReconnectingWS ? 'animate-pulse' : ''}`} />
                {isReconnectingWS ? 'Connecting...' : 'Fix Ticks'}
              </button>
            </div>

            {/* Broker Indicator & Switch - Compact */}
            <div className="flex items-center gap-1.5 px-2 py-1 bg-[#1a1a1a] rounded border border-[#333]">
              {(() => {
                // Three states: connected & ticks flowing | connected but no ticks | disconnected
                const liveAndTicking = connectedBroker && sessionHealth.websocketConnected === true && sessionHealth.marketOpen;
                const connectedButDead = connectedBroker && sessionHealth.websocketConnected === false && sessionHealth.marketOpen;
                let dotColor = 'bg-red-500';
                let label = 'Not Connected';
                let labelColor = 'text-[#666]';
                if (liveAndTicking) {
                  dotColor = 'bg-green-500 animate-pulse';
                  label = `Zerodha Live${sessionHealth.subscribedSymbols > 0 ? ` • ${sessionHealth.subscribedSymbols} sym` : ''}`;
                  labelColor = 'text-green-400';
                } else if (connectedButDead) {
                  dotColor = 'bg-red-500 animate-pulse';
                  label = 'Session Expired';
                  labelColor = 'text-red-400';
                } else if (connectedBroker === 'zerodha') {
                  dotColor = 'bg-orange-500 animate-pulse';
                  label = 'Zerodha';
                  labelColor = 'text-orange-400';
                }
                return (
                  <>
                    <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} title={sessionHealth.lastChecked ? `Last check: ${sessionHealth.lastChecked.toLocaleTimeString()}` : 'Checking session...'} />
                    <span className={`text-[10px] ${labelColor}`} data-testid="broker-status-label">{label}</span>
                  </>
                );
              })()}
              {!connectedBroker ? (
                <button
                  onClick={handleOneClickAuth}
                  disabled={zerodhaAuthStatus.loading}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-semibold transition-colors ${
                    zerodhaAuthStatus.can_auth 
                      ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                      : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                  }`}
                  data-testid="one-click-auth-btn"
                  title={zerodhaAuthStatus.message || 'Connect to Zerodha'}
                >
                  <Zap className="w-3 h-3" />
                  {zerodhaAuthStatus.loading ? 'Loading...' : 
                   zerodhaAuthStatus.can_auth ? 'Connect Zerodha' : 
                   !zerodhaAuthStatus.has_admin_permission ? 'Need Permission' : 'Subscribe'}
                </button>
              ) : (
                <>
                  {/* Daily Auth Button - For Re-Authentication */}
                  <button
                    onClick={handleOneClickAuth}
                    disabled={zerodhaAuthStatus.loading}
                    className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-500 rounded text-[9px] font-semibold text-white transition-all"
                    data-testid="daily-auth-btn"
                    title="Click for daily Zerodha authentication"
                  >
                    <Zap className="w-3 h-3" />
                    Daily Auth
                  </button>
                  <button
                    onClick={handleSwitchBroker}
                    className="text-[9px] text-red-400 hover:text-red-300"
                    data-testid="switch-broker-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
            
            {/* Edit API Credentials Button */}
            <button
              onClick={() => {
                fetchApiCredentials();
                setShowEditApiModal(true);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg text-[10px] font-semibold text-white transition-all shadow-lg shadow-cyan-500/20"
              data-testid="edit-api-btn"
              title="Edit Zerodha API Credentials"
            >
              <Key className="w-3 h-3" />
              <span className="hidden sm:inline">API Keys</span>
            </button>
            
            {/* PRO Button - Go Premium */}
            {(!hasPremium || !hasPremium()) && (
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 hover:from-amber-400 hover:via-yellow-400 hover:to-orange-400 rounded-lg text-[10px] font-bold text-black transition-all shadow-lg shadow-amber-500/30 animate-pulse"
                data-testid="go-pro-btn"
                title="Upgrade to Premium"
              >
                <Crown className="w-3.5 h-3.5" />
                <span>GO PRO</span>
                <Sparkles className="w-3 h-3" />
              </button>
            )}

            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {/* Row 2: Tools & Features */}
        <div className="h-10 flex items-center justify-between px-4 overflow-visible">
          <div className="flex items-center gap-2 overflow-x-auto flex-1">
            {/* Refresh */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { fetchInstruments("NSE"); fetchInstruments("NFO"); }}
              className="h-7 px-2 text-[#A1A1AA] hover:text-white"
              data-testid="refresh-instruments"
              title="Refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>

            {/* Column Settings */}
            <ColumnSettings columns={visibleColumns} setColumns={setVisibleColumns} />

            {/* Tick Log */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!checkApprovalAndProceed()) return;
                if (checkPremiumAccess("Tick Log")) {
                  setShowTickHistory(true);
                }
              }}
              className="h-7 px-2 text-[#A1A1AA] hover:text-white hover:bg-blue-600/20"
              data-testid="open-tick-history"
            >
              <BarChart3 className="w-3 h-3 mr-1" />
              <span className="text-[10px]">Tick Log</span>
            </Button>

            {/* Smart Filter History - Replaces Activity Log */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!checkApprovalAndProceed()) return;
                fetchSmartFilterAlerts();
                setShowSmartFilterHistory(true);
              }}
              className="h-7 px-2 text-[#A1A1AA] hover:text-white hover:bg-purple-600/20"
              data-testid="open-smart-filter-history"
            >
              <Filter className="w-3 h-3 mr-1" />
              <span className="text-[10px]">Smart Alert</span>
            </Button>

            {/* Pause/Play */}
            <Button
              variant={isLiveUpdating ? "ghost" : "default"}
              size="sm"
              onClick={toggleLiveUpdate}
              className={`h-7 px-2 ${!isLiveUpdating ? 'bg-yellow-600 hover:bg-yellow-700' : 'text-[#A1A1AA] hover:text-white'}`}
              data-testid="toggle-live"
              title={isLiveUpdating ? "Pause" : "Play"}
            >
              {isLiveUpdating ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </Button>

            {/* Zoom */}
            <div className="flex items-center gap-0.5 px-1 border border-[#27272A] rounded">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFontSize(prev => Math.max(70, prev - 10))}
                className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-white"
                title="Zoom out"
              >
                <ZoomOut className="w-3 h-3" />
              </Button>
              <span className="text-[9px] text-[#71717A] w-6 text-center">{fontSize}%</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFontSize(prev => Math.min(150, prev + 10))}
                className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-white"
                title="Zoom in"
              >
                <ZoomIn className="w-3 h-3" />
              </Button>
            </div>

            {/* Scanner */}
            <Button
              variant={showScannerPanel ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                if (!checkApprovalAndProceed()) return;
                if (checkPremiumAccess("Scanner")) {
                  setShowScannerPanel(!showScannerPanel);
                }
              }}
              className={`h-7 px-2 ${showScannerPanel ? 'bg-purple-600' : ''}`}
              data-testid="toggle-scanner"
            >
              <Scan className="w-3 h-3 mr-1" />
              <span className="text-[10px]">Scanner</span>
            </Button>

            {/* Alerts */}
            <Button
              variant={showAlertPanel ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                if (!checkApprovalAndProceed()) return;
                if (checkPremiumAccess("Alerts")) {
                  setShowAlertPanel(!showAlertPanel);
                }
              }}
              className={`h-7 px-2 ${showAlertPanel ? 'bg-blue-600' : ''}`}
              data-testid="toggle-alerts"
            >
              <Bell className="w-3 h-3 mr-1" />
              <span className="text-[10px]">Create Alert ({alerts.filter(a => a.enabled).length})</span>
            </Button>
            
            {/* AI Stock Scan */}
            <Button
              variant={showAlgoSetup ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                if (!checkApprovalAndProceed()) return;
                if (checkPremiumAccess("AI Stock Scan")) {
                  setShowAlgoSetup(!showAlgoSetup);
                }
              }}
              className={`h-7 px-2 ${showAlgoSetup ? 'bg-cyan-600' : ''}`}
              data-testid="toggle-algo-setup"
            >
              <Table2 className="w-3 h-3 mr-1" />
              <span className="text-[10px]">AI Stock Scan</span>
            </Button>
          </div>

          {/* Right side - Referral - need overflow-visible for dropdown panel */}
          <div className="flex items-center gap-2 overflow-visible">
            <ReferralWidget userId={currentUser?.id} user={currentUser} />
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="border-b border-[#27272A] bg-[#111111] relative">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
          <TabsList className="bg-transparent h-10 gap-2">
            <TabsTrigger 
              value="watchlist" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white transition-all duration-300"
              data-testid="tab-watchlist"
            >
              <Activity className="w-4 h-4 mr-2" />
              Watchlist
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white transition-all duration-300"
              data-testid="tab-history"
              onClick={(e) => {
                if (!hasPremium || !hasPremium()) {
                  e.preventDefault();
                  setSubscriptionFeature("History");
                  setShowSubscriptionModal(true);
                }
              }}
            >
              <History className="w-4 h-4 mr-2" />
              History (10 Days)
            </TabsTrigger>
            
            {/* Go Pro Tab - Only for non-premium users */}
            {(!hasPremium || !hasPremium()) && (
              <TabsTrigger 
                value="go-pro" 
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold hover:from-amber-400 hover:to-orange-400 transition-all duration-300 animate-pulse"
                data-testid="tab-go-pro"
                onClick={(e) => {
                  e.preventDefault();
                  setShowSubscriptionModal(true);
                }}
              >
                <Crown className="w-4 h-4 mr-2" />
                Go PRO
                <Sparkles className="w-3 h-3 ml-1" />
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Watchlist View Toggle - Only show when on watchlist tab */}
          {activeTab === "watchlist" && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {/* Server Alerts Button */}
              <button
                onClick={() => setShowServerAlerts(!showServerAlerts)}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all border ${
                  showServerAlerts 
                    ? 'bg-yellow-600 text-white border-yellow-500' 
                    : 'bg-[#0a0a0a] text-[#71717A] border-[#27272A] hover:text-yellow-400 hover:border-yellow-500/50'
                }`}
                title="Server-side Alerts - Works 24/7 even when app is closed"
                data-testid="server-alerts-toggle"
              >
                <BellRing className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Server Alert</span>
              </button>
              
              {/* Telegram Settings Button */}
              <button
                onClick={() => setShowTelegramSettings(true)}
                className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all border ${
                  telegramCredentials.bot_token && telegramCredentials.chat_id
                    ? 'bg-purple-600/20 text-purple-400 border-purple-500/50' 
                    : 'bg-[#0a0a0a] text-[#71717A] border-[#27272A] hover:text-purple-400 hover:border-purple-500/50'
                }`}
                title="Configure Telegram for alerts"
                data-testid="telegram-settings-toggle"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.36-.49.99-.75 3.88-1.69 6.47-2.8 7.77-3.35 3.7-1.54 4.47-1.81 4.97-1.82.11 0 .35.03.51.18.13.12.17.28.18.45-.01.06.01.24 0 .38z"/>
                </svg>
                <span className="hidden sm:inline">Telegram</span>
              </button>
              
              {/* Watchlist Toggle */}
              <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-lg p-0.5 border border-[#27272A]">
                <button
                  onClick={() => setActiveWatchlistView('default')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    activeWatchlistView === 'default' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-[#71717A] hover:text-white hover:bg-[#1a1a1a]'
                  }`}
                  data-testid="toggle-default-watchlist"
                >
                  Watchlist ({watchlistTokens.length})
                </button>
                <button
                  onClick={() => setActiveWatchlistView('my_watchlist')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    activeWatchlistView === 'my_watchlist' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-[#71717A] hover:text-white hover:bg-[#1a1a1a]'
                  }`}
                  data-testid="toggle-my-watchlist"
                >
                  My Watchlist ({myWatchlistTokens.length}/150)
                </button>
              </div>
            </div>
          )}
        </Tabs>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={() => {
          refreshUser && refreshUser();
          setShowSubscriptionModal(false);
        }}
        featureName={subscriptionFeature}
      />

      {/* Admin Approval Modal - Shows when unapproved user tries to access features */}
      <AdminApprovalModal 
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {activeTab === "watchlist" ? (
          <>
            {/* Tick Data Table */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Stats & Filter Bar */}
              <div className="h-10 flex items-center gap-4 px-4 bg-[#111111] border-b border-[#27272A] flex-shrink-0">
                <span className="text-xs text-[#71717A]">
                  Watching: <span className="text-white font-medium">{subscribedTokens.length}</span> / 150
                </span>
                <span className="text-xs text-[#71717A]">
                  Showing: <span className="text-white font-medium">{ticksArray.length}</span>
                </span>
                <span className="text-xs text-[#71717A]">
                  Agg Buys: <span className="text-green-400 font-medium">{ticksArray.filter(t => t.agg_buy > 0).length}</span>
                </span>
                <span className="text-xs text-[#71717A]">
                  Agg Sells: <span className="text-red-400 font-medium">{ticksArray.filter(t => t.agg_sell > 0).length}</span>
                </span>
                
                {/* Broker Indicator */}
                <span className="text-xs flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#1a1a1a] border border-[#333]">
                  <span className={`w-1.5 h-1.5 rounded-full ${connectedBroker ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className={connectedBroker === 'zerodha' ? 'text-orange-400' : 'text-[#71717A]'}>
                    {connectedBroker === 'zerodha' ? 'Zerodha Kite' : 'No Broker'}
                  </span>
                  {connectedBroker === 'zerodha' && <span className="text-[10px] text-orange-600">(5-lvl)</span>}
                </span>
                
                {/* 1-Click Auth Button - Prominent when not connected */}
                {!connectedBroker && (
                  <Button
                    onClick={handleOneClickAuth}
                    size="sm"
                    className="h-7 px-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-xs font-semibold shadow-lg shadow-orange-500/20 animate-pulse"
                    data-testid="watchlist-one-click-auth"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    1-Click Zerodha Login
                  </Button>
                )}
                
                
                <div className="ml-auto flex items-center gap-2">
                  {/* Table Filter */}
                  <div className="relative">
                    <Filter className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#52525B]" />
                    <Input
                      value={tableFilter}
                      onChange={(e) => setTableFilter(e.target.value)}
                      placeholder="Filter table..."
                      className="pl-7 pr-2 h-7 w-40 bg-[#0A0A0A] border-[#27272A] text-white text-xs"
                      data-testid="table-filter"
                    />
                  </div>
                  
                  <span className="text-xs text-[#52525B]">Ctrl+T for Tick History</span>
                </div>
              </div>

              {/* Server Alert Manager Panel */}
              {showServerAlerts && (
                <div className="p-4 border-b border-[#27272A] bg-[#0a0a0a]">
                  <ServerAlertManager 
                    watchlistSymbols={(ticksArray || [])
                      .filter(t => t && t.tradingsymbol)
                      .map(t => ({ tradingsymbol: t.tradingsymbol, token: t.token || t.instrument_token }))}
                    onClose={() => setShowServerAlerts(false)}
                  />
                </div>
              )}

              {/* Table */}
              <div className="flex-1 overflow-auto">
                {ticksArray.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Search className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#71717A] text-lg mb-2">No symbols added</p>
                      <p className="text-[#52525B] text-sm">Search and add symbols to start tracking</p>
                      <p className="text-[#52525B] text-xs mt-2">Try: NIFTY, BANKNIFTY, 25600, RELIANCE</p>
                    </div>
                  </div>
                ) : (
                  <table className="data-table" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        {visibleColumns.symbol && (
                          <ResizableHeader 
                            columnKey="symbol" 
                            label="Symbol" 
                            width={columnWidths.symbol}
                            onResize={handleColumnResize}
                            sortable={false}
                          />
                        )}
                        {visibleColumns.ltp && (
                          <ResizableHeader 
                            columnKey="ltp" 
                            label="LTP" 
                            width={columnWidths.ltp}
                            onResize={handleColumnResize}
                            sortable={true}
                            onSort={() => handleSort('last_price')}
                            sortField={sortField === 'last_price' ? 'ltp' : null}
                            sortDirection={sortDirection}
                          />
                        )}
                        {visibleColumns.change && (
                          <ResizableHeader columnKey="change" label="Change" width={columnWidths.change} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.open && (
                          <ResizableHeader columnKey="open" label="Open" width={columnWidths.open} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.high && (
                          <ResizableHeader columnKey="high" label="High" width={columnWidths.high} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.low && (
                          <ResizableHeader columnKey="low" label="Low" width={columnWidths.low} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.volume && (
                          <ResizableHeader 
                            columnKey="volume" 
                            label="Volume" 
                            width={columnWidths.volume}
                            onResize={handleColumnResize}
                            sortable={true}
                            onSort={() => handleSort('volume_traded')}
                            sortField={sortField === 'volume_traded' ? 'volume' : null}
                            sortDirection={sortDirection}
                          />
                        )}
                        {visibleColumns.vwap && (
                          <ResizableHeader columnKey="vwap" label="VWAP" width={columnWidths.vwap} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.buyQty && (
                          <ResizableHeader columnKey="buyQty" label="Buy Qty" width={columnWidths.buyQty} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.sellQty && (
                          <ResizableHeader columnKey="sellQty" label="Sell Qty" width={columnWidths.sellQty} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.bid && (
                          <ResizableHeader columnKey="bid" label="Bid" width={columnWidths.bid} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.ask && (
                          <ResizableHeader columnKey="ask" label="Ask" width={columnWidths.ask} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.ltq && (
                          <ResizableHeader 
                            columnKey="ltq" 
                            label="LTQ" 
                            width={columnWidths.ltq}
                            onResize={handleColumnResize}
                            sortable={true}
                            onSort={() => handleSort('last_traded_quantity')}
                            sortField={sortField === 'last_traded_quantity' ? 'ltq' : null}
                            sortDirection={sortDirection}
                          />
                        )}
                        {visibleColumns.oi && (
                          <ResizableHeader 
                            columnKey="oi" 
                            label="OI" 
                            width={columnWidths.oi}
                            onResize={handleColumnResize}
                            sortable={true}
                            onSort={() => handleSort('oi')}
                            sortField={sortField === 'oi' ? 'oi' : null}
                            sortDirection={sortDirection}
                          />
                        )}
                        {visibleColumns.oiChange && (
                          <ResizableHeader columnKey="oiChange" label="OI Chg" width={columnWidths.oiChange} onResize={handleColumnResize} />
                        )}
                        {visibleColumns.aggBuy && (
                          <th className="py-2 px-2 text-left text-[10px] font-semibold text-[#71717A] uppercase border-b border-[#27272A] bg-green-900/20" style={{ width: `${columnWidths.aggBuy}px` }}>Agg Buy</th>
                        )}
                        {visibleColumns.aggSell && (
                          <th className="py-2 px-2 text-left text-[10px] font-semibold text-[#71717A] uppercase border-b border-[#27272A] bg-red-900/20" style={{ width: `${columnWidths.aggSell}px` }}>Agg Sell</th>
                        )}
                        <th className="py-2 px-2 border-b border-[#27272A]" style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticksArray.map((tick) => (
                        <TickerRow
                          key={tick.instrument_token}
                          tick={tick}
                          instrument={instruments[tick.instrument_token]}
                          onRemove={handleUnsubscribe}
                          maxBuyQty={maxBuyQty}
                          maxSellQty={maxSellQty}
                          maxLtq={maxLtq}
                          maxOi={maxOi}
                          maxOiChange={maxOiChange}
                          visibleColumns={visibleColumns}
                          aggStreak={aggStreaks[tick.instrument_token] || 0}
                          columnWidths={columnWidths}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Scanner Panel */}
            {showScannerPanel && (
              <ScannerPanel 
                instruments={instruments}
                subscribedTokens={subscribedTokens}
                tickData={tickData}
                onClose={() => setShowScannerPanel(false)}
              />
            )}
            
            {/* AI Signals Panel - Full Width */}
            {showAlgoSetup && (
              <div className={`${algoFullScreen ? 'fixed inset-0 z-50' : 'flex-1'} border-l border-[#27272A] bg-[#0a0e17] flex flex-col`}>
                <AlgoSetupTab 
                  onClose={() => setShowAlgoSetup(false)}
                  isFullScreen={algoFullScreen}
                  onToggleFullScreen={() => setAlgoFullScreen(!algoFullScreen)}
                  highlightedCells={algoHighlightedCells}
                />
              </div>
            )}

            {/* Alerts Panel */}
            {showAlertPanel && !showAlgoSetup && (
              <div className="w-80 border-l border-[#27272A] bg-[#111111] flex flex-col flex-shrink-0">
                <div className="p-4 border-b border-[#27272A]">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Alert Manager
                  </h2>
                </div>

                <div className="p-4 border-b border-[#27272A] space-y-3">
                  <h3 className="text-sm font-medium text-[#A1A1AA]">Create New Smart Alert</h3>
                  
                  <Select 
                    value={newAlert.instrument_token?.toString() || ""} 
                    onValueChange={(val) => {
                      const token = parseInt(val);
                      const inst = instruments[token];
                      setNewAlert(prev => ({
                        ...prev,
                        instrument_token: token,
                        symbol: inst?.tradingsymbol || String(token)
                      }));
                    }}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="alert-symbol-select">
                      <SelectValue placeholder="Select symbol" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(tickData).length > 0 ? (
                        Object.keys(tickData).map(token => (
                          <SelectItem key={token} value={token}>
                            {instruments[parseInt(token)]?.tradingsymbol || token}
                          </SelectItem>
                        ))
                      ) : subscribedTokens.length > 0 ? (
                        subscribedTokens.map(token => (
                          <SelectItem key={token} value={token.toString()}>
                            {instruments[token]?.tradingsymbol || token}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>No symbols in watchlist</SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  <Select 
                    value={newAlert.alert_type} 
                    onValueChange={(val) => setNewAlert(prev => ({ ...prev, alert_type: val }))}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {alertTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    type="number"
                    value={newAlert.threshold || ""}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
                    placeholder="Threshold value"
                    className="bg-[#0A0A0A] border-[#27272A] text-white"
                  />

                  <Button onClick={handleCreateAlert} className="w-full bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Alert
                  </Button>
                </div>

                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-2">
                    {alerts.length === 0 ? (
                      <p className="text-[#52525B] text-sm text-center py-4">No smart alerts configured</p>
                    ) : (
                      alerts.map(alert => (
                        <AlertItem
                          key={alert.id}
                          alert={alert}
                          onDelete={deleteAlert}
                          onToggle={handleToggleAlert}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>

                {triggeredAlerts.length > 0 && (
                  <div className="border-t border-[#27272A] max-h-48 overflow-auto">
                    <div className="p-4 border-b border-[#27272A] sticky top-0 bg-[#111111]">
                      <h3 className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                        <Volume2 className="w-4 h-4" />
                        Recent Triggers ({triggeredAlerts.length})
                      </h3>
                    </div>
                    <div className="p-4 space-y-2">
                      {triggeredAlerts.slice(-10).reverse().map((alert, i) => (
                        <div key={i} className="p-2 bg-yellow-900/10 border border-yellow-900/30 rounded text-xs">
                          <p className="text-yellow-500">{alert.message}</p>
                          <p className="text-[#52525B] mt-1">{new Date(alert.timestamp).toLocaleTimeString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <HistoryTab instruments={instruments} />
        )}
      </div>
      
      {/* AI Signals Monitor - Runs in background even when AI Signals tab is closed */}
      <AlgoMonitor 
        isAuthenticated={!!currentUser} 
        onAlertReceived={(alerts, newCells) => {
          setAlgoHighlightedCells(newCells);
          // Optionally open the AI Signals panel when alerts come in
          if (alerts.length > 0 && !showAlgoSetup) {
            // Show notification that there are updates
          }
        }}
      />
      
      {/* Smart Filter Alert History Modal */}
      {showSmartFilterHistory && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#111827] border border-purple-500/30 rounded-2xl p-6 w-[95%] max-w-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Smart Filter Alert History</h3>
                  <p className="text-xs text-[#6b7a99]">Your triggered alerts with tick data</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={fetchSmartFilterAlerts}
                  disabled={smartFilterLoading}
                  className="text-xs"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${smartFilterLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <button
                  onClick={() => setShowSmartFilterHistory(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Alert List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {smartFilterLoading ? (
                <div className="flex items-center justify-center py-10">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                </div>
              ) : smartFilterAlerts.length === 0 ? (
                <div className="text-center py-10">
                  <Filter className="w-12 h-12 text-[#3a4a6a] mx-auto mb-3" />
                  <p className="text-sm text-[#6b7a99]">No smart filter alerts yet</p>
                  <p className="text-xs text-[#4a5a7a] mt-1">Alerts will appear here when your filters trigger</p>
                </div>
              ) : (
                smartFilterAlerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-[#0a0e17] border border-[#1a2540] rounded-lg hover:border-purple-500/30 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white">{alert.symbol}</span>
                        <span className="text-[10px] text-[#6b7a99]">Token: {alert.token}</span>
                      </div>
                      <span className="text-[10px] text-purple-400">
                        {alert.timestamp_ist ? new Date(alert.timestamp_ist).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }) : 'N/A'}
                      </span>
                    </div>
                    
                    {/* Filters that triggered */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(alert.filter_names || []).map((name, i) => (
                        <span 
                          key={i}
                          className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-[9px] rounded-full"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                    
                    {/* Tick Data */}
                    <div className="grid grid-cols-4 gap-2 text-[10px]">
                      <div className="bg-[#111827] p-1.5 rounded">
                        <p className="text-[#6b7a99]">LTP</p>
                        <p className="text-cyan-400 font-mono">{alert.ltp?.toFixed(2) || '-'}</p>
                      </div>
                      <div className="bg-[#111827] p-1.5 rounded">
                        <p className="text-[#6b7a99]">LTQ</p>
                        <p className="text-green-400 font-mono">{alert.ltq?.toLocaleString() || '-'}</p>
                      </div>
                      <div className="bg-[#111827] p-1.5 rounded">
                        <p className="text-[#6b7a99]">Volume</p>
                        <p className="text-blue-400 font-mono">{alert.volume?.toLocaleString() || '-'}</p>
                      </div>
                      <div className="bg-[#111827] p-1.5 rounded">
                        <p className="text-[#6b7a99]">OI</p>
                        <p className="text-amber-400 font-mono">{alert.oi?.toLocaleString() || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center gap-2 mt-2">
                      {alert.sent_to_admin && (
                        <span className="text-[9px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">Admin Notified</span>
                      )}
                      {alert.sent_to_user && (
                        <span className="text-[9px] text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">User Notified</span>
                      )}
                      {alert.stored_only && (
                        <span className="text-[9px] text-gray-400 bg-gray-500/10 px-1.5 py-0.5 rounded">Stored Only</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-[#1a2540] flex justify-between items-center">
              <p className="text-[10px] text-[#6b7a99]">{smartFilterAlerts.length} alerts</p>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('auth_token');
                    await apiClient.delete('/user/smart-filter/alerts?older_than_days=7', {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Old alerts cleared');
                    fetchSmartFilterAlerts();
                  } catch { toast.error('Failed to clear alerts'); }
                }}
                className="text-xs text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear Old (7d+)
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit API Credentials Modal */}
      {showEditApiModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-[#111827] border border-cyan-500/30 rounded-2xl p-6 w-[90%] max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Key className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Edit API Credentials</h3>
                  <p className="text-xs text-[#6b7a99]">Update your Zerodha Kite API keys</p>
                </div>
              </div>
              <button
                onClick={() => setShowEditApiModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* API Key */}
              <div>
                <label className="text-xs text-cyan-400 font-medium block mb-1.5">
                  API Key *
                </label>
                <input
                  type="text"
                  value={apiCredentials.api_key}
                  onChange={(e) => setApiCredentials(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your Kite API Key"
                  className="w-full px-3 py-2.5 bg-[#0a0e17] border border-[#1a2540] rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="api-key-input"
                />
              </div>
              
              {/* API Secret */}
              <div>
                <label className="text-xs text-cyan-400 font-medium block mb-1.5">
                  API Secret (leave empty to keep current)
                </label>
                <input
                  type="password"
                  value={apiCredentials.api_secret}
                  onChange={(e) => setApiCredentials(prev => ({ ...prev, api_secret: e.target.value }))}
                  placeholder="Enter new API Secret or leave empty"
                  className="w-full px-3 py-2.5 bg-[#0a0e17] border border-[#1a2540] rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="api-secret-input"
                />
              </div>
              
              {/* TOTP Secret - Hidden by default, advanced feature */}
              {/* 
              <div>
                <label className="text-xs text-cyan-400 font-medium block mb-1.5">
                  TOTP Secret (Advanced - Optional)
                </label>
                <input
                  type="password"
                  value={apiCredentials.totp_secret}
                  onChange={(e) => setApiCredentials(prev => ({ ...prev, totp_secret: e.target.value }))}
                  placeholder="Enter TOTP Secret for auto-login (optional)"
                  className="w-full px-3 py-2.5 bg-[#0a0e17] border border-[#1a2540] rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  data-testid="totp-secret-input"
                />
                <p className="text-[10px] text-[#6b7a99] mt-1">
                  Optional: The base32 secret from your authenticator app for auto-2FA
                </p>
              </div>
              */}
              
              {/* Universal Redirect URL - IMPORTANT */}
              <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-cyan-400 font-semibold flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5" />
                    Redirect URL (Copy this to Kite App)
                  </p>
                  <button
                    onClick={() => {
                      copyToClipboard(`${window.location.origin}/api/auth/zerodha/callback`, 'Redirect URL copied!');
                    }}
                    className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1 px-2 py-0.5 bg-cyan-500/10 rounded"
                    data-testid="copy-redirect-url-btn"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                </div>
                <code className="block text-[10px] text-white bg-[#0a0e17] p-2 rounded font-mono break-all select-all" data-testid="redirect-url-display">
                  {`${window.location.origin}/api/auth/zerodha/callback`}
                </code>
                <p className="text-[10px] text-[#6b7a99] mt-2">
                  ⚠️ Copy this URL and paste it in your <a href="https://kite.trade" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">Kite Developer App</a>'s "Redirect URL" field.
                </p>
              </div>
              
              {/* Info Box */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-[10px] text-amber-400">
                  <strong>Note:</strong> Get your API credentials from{' '}
                  <a 
                    href="https://kite.trade" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-300"
                  >
                    kite.trade
                  </a>
                  . After saving, you may need to reconnect to Zerodha.
                </p>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowEditApiModal(false)}
                  className="flex-1 px-4 py-2.5 bg-[#1a2540] hover:bg-[#2a3a60] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveApiCredentials}
                  disabled={apiCredentialsLoading || !apiCredentials.api_key}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="save-api-btn"
                >
                  {apiCredentialsLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save Credentials
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Telegram Settings Modal */}
      {showTelegramSettings && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowTelegramSettings(false)}>
          <div 
            className="bg-[#0a0a0a] border border-[#333] rounded-xl max-w-md w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#333] bg-gradient-to-r from-purple-900/30 to-blue-900/30">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.36-.49.99-.75 3.88-1.69 6.47-2.8 7.77-3.35 3.7-1.54 4.47-1.81 4.97-1.82.11 0 .35.03.51.18.13.12.17.28.18.45-.01.06.01.24 0 .38z"/>
                </svg>
                <h3 className="text-white font-bold">Telegram Settings</h3>
              </div>
              <button
                onClick={() => setShowTelegramSettings(false)}
                className="text-[#666] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
              {telegramLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                </div>
              ) : (
                <>
                  {/* Instructions */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-300 space-y-2">
                    <p className="font-semibold">How to get your Telegram credentials:</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-400/80">
                      <li>Open Telegram and search for <span className="text-white">@BotFather</span></li>
                      <li>Send <code className="bg-black/30 px-1 rounded">/newbot</code> and follow instructions</li>
                      <li>Copy the <span className="text-white">Bot Token</span> provided</li>
                      <li>Search for <span className="text-white">@userinfobot</span> to get your Chat ID</li>
                    </ol>
                  </div>
                  
                  {/* Bot Token */}
                  <div>
                    <label className="text-xs text-[#888] block mb-1.5">Bot Token</label>
                    <input 
                      type="text"
                      value={telegramCredentials.bot_token}
                      onChange={(e) => setTelegramCredentials(prev => ({...prev, bot_token: e.target.value}))}
                      placeholder="123456789:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                      className="w-full h-9 px-3 text-sm bg-[#111] border border-[#333] rounded-lg text-white placeholder:text-[#555] focus:border-purple-500 focus:outline-none"
                      data-testid="telegram-bot-token-input"
                    />
                  </div>
                  
                  {/* Chat ID */}
                  <div>
                    <label className="text-xs text-[#888] block mb-1.5">Chat ID</label>
                    <input 
                      type="text"
                      value={telegramCredentials.chat_id}
                      onChange={(e) => setTelegramCredentials(prev => ({...prev, chat_id: e.target.value}))}
                      placeholder="123456789"
                      className="w-full h-9 px-3 text-sm bg-[#111] border border-[#333] rounded-lg text-white placeholder:text-[#555] focus:border-purple-500 focus:outline-none"
                      data-testid="telegram-chat-id-input"
                    />
                  </div>
                  
                  {/* Status indicator */}
                  {telegramCredentials.bot_token && telegramCredentials.chat_id && (
                    <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 rounded-lg px-3 py-2">
                      <Check className="w-4 h-4" />
                      Telegram credentials configured
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Footer */}
            <div className="flex items-center gap-2 p-4 border-t border-[#333] bg-[#080808]">
              <button
                onClick={testTelegramConnection}
                disabled={!telegramCredentials.bot_token || !telegramCredentials.chat_id}
                className="px-4 py-2 bg-[#1a1a1a] text-[#888] hover:text-white border border-[#333] rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="test-telegram-btn"
              >
                Test Connection
              </button>
              <div className="flex-1" />
              <button
                onClick={() => setShowTelegramSettings(false)}
                className="px-4 py-2 text-[#888] hover:text-white text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveTelegramSettings}
                disabled={telegramSaving || !telegramCredentials.bot_token || !telegramCredentials.chat_id}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="save-telegram-btn"
              >
                {telegramSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer Credit */}
      <div className="fixed bottom-3 right-4 text-sm text-white/70 z-10 font-medium tracking-wide">
        Made by Mr.MarketX
      </div>
    </div>
  );
}
